/**
 * NW New Bundle
 * Product PDP: bundle option radios ↔ free-gift cards (lock state + unlock messaging).
 *
 * Public API (extend from other scripts or app blocks):
 *   window.NwNewBundle.sync() — apply ?boxes= to radios, then CTA + URL (after section reload)
 *   window.NwNewBundle.syncCta()
 *   window.NwNewBundle.getSelectedBundleItemCount() — active tier box count (data-item-count)
 *   Free-gift lock/unlock per box-quantity card is rendered server-side by
 *   'nw-new-bundle-option' (each card gets its own tier's unlock state) — no JS sync needed.
 *   `.update-tree-number-bund` — synced from checked `nw-new-bundle-option` radio `data-item-count`
 *   `.percentage-info-container__percentage` — synced from checked option `data-subscription-discount-percent`
 *   `.nw-bundle-new-onetime` — count from radio `data-item-count`; price from `data-compare-price` (tier compare total) when set, else `data-price`
 *   window.NwNewBundle.getActivePurchaseType() — 'subscription' | 'onetime'
 *   window.NwNewBundle.syncBundleQuantityUrl() — write ?boxes=… from current radio
 *   window.NwNewBundle.syncPurchaseTypeUrl() — optional: write ?purchase=… from current mode (not auto on load)
 *   window.NwNewBundle.openFlavorsModal() / closeFlavorsModal()
 *   window.NwNewBundle.getFlavorsSelectionState() — { lines, totalQty, distinctWithQty, qtyByFlavorId }
 *   window.NwNewBundle.flavorsSelectionMeetsRequirements() — CTA / min rules
 *   Add data-nw-bundle-flavors-open + data-purchase-type="subscription"|"onetime" on each popup trigger.
 *   Optional `.nw-new-bundle-purchase-type` block: radios `name="nw-purchase-type"` drive `activePurchaseType`, update every
 *   `[data-nw-new-bundle-cta]`'s data-purchase-type, and `/ea` copy from bundle radios `data-each-price` / `data-each-compare-price`.
 *   With that block, CTA shows one price line only (no compare strike): subscription → `data-price`; one-time → `data-compare-price` (fallback `data-price` if empty).
 *   Compare-at `<s>` on subscription row only; one-time row shows `data-each-compare-price` as plain text `/ea` (no strike).
   *   Flavors footer CTA ([data-main-cta]): clears cart (optional), one POST /cart/add.js with items[]
   *   in order flavors → gifts → tree (+ selling_plan on flavors when subscription); skips excluded / free-shipping tier gifts.
   *   Optional block settings + podcast LP metaobject: discount code (subscription only)
   *   + subscription discount % (display-only, decimals OK). Drawer/upsell: POST /cart/update.js
   *   (merged codes) + GET /discount/CODE fallback. Checkout redirect: /checkout?discount=CODE
   *   so the code is in the URL (Ajax cart discounts often drop on iOS WebKit).
   *   After cart add: show checkout upsell modal when enabled, else redirect to checkout.
 *   Javvy only: `data-discount-code` on javvy radios (`custom.discount_code` per 2/4/6/8-box
 *   variant). Subscription ATC applies the code for **current flavor-modal qty** (2/4/6/8+),
 *   not the pack that opened the popup. Flavor lines keep the default selling plan.
 *   Gum / maxxing ignore the attr and keep the modal `50-OFF`.
 *   Javvy sends no code when `[data-nw-tier-pricing-function="true"]` is rendered (gum-tier-pricing function prices the boxes).
 *   Tier used for flavors = bundle radio whose `data-item-count` matches **sum of flavor line qty** (exact, else ceil then floor among tiers); if sum is 0, uses checked PDP tier.
 *   Openers `[data-nw-bundle-flavors-open]` may carry optional `data-box-quantity` — positive integer primes flavor qty (does not change `nw-new-bundle-option` / `?boxes=`). If absent, priming follows the checked tier’s `data-item-count` / `?boxes=`. Priming distributes in chunks of 2 across the first flavors (e.g. 8 → 2+2+2+2, 6 → 2+2+2, 4 → 2+2, 2 → 2).
 *
 * URL (replaceState, other params preserved):
 *   ?boxes=<number> — bundle tier (`data-item-count`). On load, URL can set the radio; when you
 *     change the radio, `?boxes=` is updated to match the selected tier.
 *   ?purchase=subscription|onetime — set when opening the popup from a trigger with data-purchase-type;
 *     removed when the popup closes. If present on load, it sets the modal pricing mode.
 */
(function () {
  'use strict';

  if (window.__nwNewBundleInstalled) return;
  window.__nwNewBundleInstalled = true;

  // This file's own <script> tag (nw-new-bundle-flavors-global section) is
  // rendered in the body, after Shrine's CDN main.js in <head> — by the time
  // this runs, window.fetch is already Shrine-wrapped, so capturing our own
  // reference here would be too late to bypass it. cart-free-gifts.js loads
  // earlier (before Shrine) and exposes the pristine one for exactly this;
  // fall back to the live (Shrine-wrapped) fetch only if that isn't present.
  const _fetch = window.__originalFetch || window.fetch.bind(window);

  // ---------------------------------------------------------------------------
  // Configuration (DOM contract with Liquid)
  // ---------------------------------------------------------------------------

  const SELECTOR = {
    bundleRadio: '.nw-new-bundle-option input[name="nw-new-bundle-option"]',
    ctaButton: '[data-nw-new-bundle-cta]',
    ctaTrack: '[data-nw-cta-track]',
    ctaPriceCurrent: '.nw-new-bundle-cta-price-current',
    ctaPriceCompare: '.nw-new-bundle-cta-price-compare',
    flavorsModal: '#nw-new-bundle-flavors-modal',
    flavorsModalClose: '[data-nw-bundle-flavors-close]',
    flavorsModalOpen: '[data-nw-bundle-flavors-open]',
    purchaseTypeWidget: '.nw-new-bundle-purchase-type',
    checkoutUpsellRoot: '#nw-checkout-upsell-root',
    checkoutUpsellModal: '#nw-new-bundle-checkout-upsell-modal',
    checkoutUpsellAccept: '[data-nw-checkout-upsell-accept]',
    checkoutUpsellDecline: '[data-nw-checkout-upsell-decline]',
    checkoutUpsellClose: '[data-nw-checkout-upsell-close]',
    percentageInfo: '.percentage-info-container__percentage',
  };

  const DATA_ATTR = {
    itemCount: 'data-item-count',
    price: 'data-price',
    comparePrice: 'data-compare-price',
    eachPrice: 'data-each-price',
    eachComparePrice: 'data-each-compare-price',
    boxQuantity: 'data-box-quantity',
    discountCode: 'data-discount-code',
    subscriptionDiscountPercent: 'data-subscription-discount-percent',
  };

  const CLASS_NAME = {
    flavorsModalOpen: 'is-open',
    bodyModalLock: 'nw-new-bundle-flavors-modal-open',
    checkoutUpsellModalOpen: 'is-open',
    bodyCheckoutUpsellLock: 'nw-new-bundle-checkout-upsell-open',
  };

  const RADIO_NAME = 'nw-new-bundle-option';

  /** PDP purchase-type radios inside {@link SELECTOR.purchaseTypeWidget} (bundle-main-product-sub optional block). */
  const PURCHASE_TYPE_RADIO_NAME = 'nw-purchase-type';

  /** Query param for bundle tier quantity (`data-item-count`, e.g. 2 / 4 / 6 boxes). */
  const URL_PARAM_BUNDLE_BOXES = 'boxes';

  /** Query param for flavors modal purchase mode (must match `data-purchase-type` on CTA triggers). */
  const URL_PARAM_PURCHASE_TYPE = 'purchase';

  const PURCHASE_SUBSCRIPTION = 'subscription';

  const PURCHASE_ONETIME = 'onetime';

  /** @type {'subscription' | 'onetime'} */
  let activePurchaseType = PURCHASE_SUBSCRIPTION;
  /** @type {boolean | null} Per-PDP override from NW New Bundle Flavors block; null = global modal default */
  let flavorsPdpClearCartOverride = null;

  // ---------------------------------------------------------------------------
  // Types (JSDoc only)
  // ---------------------------------------------------------------------------

  /**
   * @typedef {{ boxes: number, unlocked: number }} NwBundleTier
   * boxes: variant "control" / quantity (e.g. 2, 4, 6).
   * unlocked: how many gift slots this tier unlocks (metafield).
   */

  // ---------------------------------------------------------------------------
  // Pure helpers
  // ---------------------------------------------------------------------------

  /**
   * @param {string | null | undefined} raw
   * @returns {number}
   */
  function parsePositiveInt(raw) {
    const n = parseInt(String(raw ?? '0'), 10);
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }

  /**
   * @param {string | null | undefined} raw
   * @returns {number}
   */
  function parsePositiveFloat(raw) {
    const n = parseFloat(String(raw ?? '').trim());
    return Number.isFinite(n) && n > 0 ? n : 0;
  }

  /**
   * @returns {number | null}
   */
  function readBundleBoxesFromUrl() {
    const params = new URLSearchParams(window.location.search);
    const raw = params.get(URL_PARAM_BUNDLE_BOXES);
    if (raw === null || raw === '') return null;
    const n = parseInt(raw, 10);
    return Number.isFinite(n) && n > 0 ? n : null;
  }

  /**
   * @param {number} boxes
   */
  function writeBundleBoxesToUrl(boxes) {
    const url = new URL(window.location.href);
    if (boxes > 0) {
      url.searchParams.set(URL_PARAM_BUNDLE_BOXES, String(boxes));
    } else {
      url.searchParams.delete(URL_PARAM_BUNDLE_BOXES);
    }
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState({}, '', next);
    }
  }

  /**
   * @param {number} count
   * @returns {HTMLInputElement | null}
   */
  function findBundleRadioByItemCount(count) {
    const inputs = document.querySelectorAll(SELECTOR.bundleRadio);
    for (let i = 0; i < inputs.length; i += 1) {
      const el = inputs[i];
      if (!(el instanceof HTMLInputElement) || el.type !== 'radio') continue;
      if (parsePositiveInt(el.getAttribute(DATA_ATTR.itemCount)) === count) return el;
    }
    return null;
  }

  /**
   * If `?boxes=` matches a tier, select that radio (URL wins over Liquid default).
   */
  function applyBundleSelectionFromUrl() {
    const urlBoxes = readBundleBoxesFromUrl();
    if (urlBoxes === null) return;

    const radio = findBundleRadioByItemCount(urlBoxes);
    if (!radio) return;

    if (!radio.checked) {
      radio.checked = true;
    }
  }

  /**
   * Bundle tier used for flavor subscription pricing + `selling_plan` at checkout.
   * Javvy: highest tier whose box count is ≤ flavor qty (7 → 6-box plan, 8 → 8-box).
   * Gum / maxxing: exact `data-item-count`, else smallest tier ≥ qty, else largest < qty.
   *
   * @param {number} totalFlavorQty Sum of all flavor line quantities in the modal.
   * @returns {HTMLInputElement | null}
   */
  function getBundleRadioForFlavorTotalQty(totalFlavorQty) {
    const n = parsePositiveInt(totalFlavorQty);
    if (n <= 0) return getActiveBundleRadio();

    if (isJavvyPdp()) {
      return getJavvyDiscountRadioForQty(n) || getActiveBundleRadio();
    }

    const exact = findBundleRadioByItemCount(n);
    if (exact) return exact;

    const inputs = document.querySelectorAll(SELECTOR.bundleRadio);
    /** @type {{ el: HTMLInputElement, boxes: number }[]} */
    const tiers = [];
    inputs.forEach((el) => {
      if (!(el instanceof HTMLInputElement) || el.type !== 'radio') return;
      const boxes = parsePositiveInt(el.getAttribute(DATA_ATTR.itemCount));
      if (boxes > 0) tiers.push({ el, boxes });
    });
    if (!tiers.length) return getActiveBundleRadio();

    const ge = tiers.filter((t) => t.boxes >= n).sort((a, b) => a.boxes - b.boxes);
    if (ge.length) return ge[0].el;

    const le = tiers.filter((t) => t.boxes < n).sort((a, b) => b.boxes - a.boxes);
    if (le.length) return le[0].el;

    return getActiveBundleRadio();
  }

  /**
   * Keep `?boxes=` aligned with the checked bundle option (`data-item-count`).
   * @param {HTMLInputElement | undefined} [radioHint] If passed (e.g. from `change` target), use this
   *   radio so the URL updates from the tier the user just picked.
   */
  function syncBundleQuantityUrl(radioHint) {
    let radio = null;
    if (
      radioHint instanceof HTMLInputElement &&
      radioHint.name === RADIO_NAME &&
      radioHint.type === 'radio'
    ) {
      radio = radioHint;
    } else {
      radio = getActiveBundleRadio();
    }
    if (!radio) return;

    const count = parsePositiveInt(radio.getAttribute(DATA_ATTR.itemCount));
    if (count <= 0) return;

    const fromUrl = readBundleBoxesFromUrl();
    if (fromUrl === count) return;

    writeBundleBoxesToUrl(count);
  }

  /**
   * @returns {number}
   */
  function getSelectedBundleItemCount() {
    const radio = getActiveBundleRadio();
    if (!radio) return 0;
    return parsePositiveInt(radio.getAttribute(DATA_ATTR.itemCount));
  }

  /**
   * Tree banner(s): set `.update-tree-number-bund` from the checked bundle option in the same product area.
   */
  function syncTreePlantingCountWithBundle() {
    document.querySelectorAll('.update-tree-number-bund').forEach((span) => {
      const scope =
        span.closest('.product__info-wrapper') ||
        span.closest('.product') ||
        span.closest('.shopify-section') ||
        document;
      const checked = scope.querySelector(`${SELECTOR.bundleRadio}:checked`);
      const radio =
        checked instanceof HTMLInputElement
          ? checked
          : scope.querySelector(SELECTOR.bundleRadio);
      if (!(radio instanceof HTMLInputElement)) return;
      span.textContent = String(parsePositiveInt(radio.getAttribute(DATA_ATTR.itemCount)));
    });
  }

  /**
   * Onetime CTA row: box count + price from active radio (`data-compare-price` when present, else `data-price`).
   */
  function syncOnetimeBundleSummaryWithBundle() {
    document.querySelectorAll('.nw-bundle-new-onetime').forEach((root) => {
      const scope =
        root.closest('.product__info-wrapper') ||
        root.closest('.product') ||
        root.closest('.shopify-section') ||
        document;
      const checked = scope.querySelector(`${SELECTOR.bundleRadio}:checked`);
      const radio =
        checked instanceof HTMLInputElement
          ? checked
          : scope.querySelector(SELECTOR.bundleRadio);
      if (!(radio instanceof HTMLInputElement)) return;

      const countEl = root.querySelector('[data-nw-onetime-bundle-count]');
      const priceEl = root.querySelector('[data-nw-onetime-bundle-price]');
      if (countEl) {
        countEl.textContent = String(parsePositiveInt(radio.getAttribute(DATA_ATTR.itemCount)));
      }
      if (priceEl) {
        const compareRaw = (radio.getAttribute(DATA_ATTR.comparePrice) ?? '').trim();
        const priceRaw = (radio.getAttribute(DATA_ATTR.price) ?? '').trim();
        priceEl.textContent = compareRaw || priceRaw;
      }
    });
  }

  /**
   * @param {string | null | undefined} raw
   * @returns {'subscription' | 'onetime' | null}
   */
  function normalizePurchaseType(raw) {
    const s = String(raw ?? '').trim().toLowerCase();
    if (s === PURCHASE_ONETIME || s === 'one-time' || s === 'one_time') return PURCHASE_ONETIME;
    if (s === PURCHASE_SUBSCRIPTION || s === 'subscribe' || s === 'sub') return PURCHASE_SUBSCRIPTION;
    return null;
  }

  /**
   * @returns {'subscription' | 'onetime' | null}
   */
  function readPurchaseTypeFromUrl() {
    const params = new URLSearchParams(window.location.search);
    return normalizePurchaseType(params.get(URL_PARAM_PURCHASE_TYPE));
  }

  /**
   * @param {'subscription' | 'onetime'} type
   */
  function writePurchaseTypeToUrl(type) {
    const url = new URL(window.location.href);
    if (type === PURCHASE_SUBSCRIPTION || type === PURCHASE_ONETIME) {
      url.searchParams.set(URL_PARAM_PURCHASE_TYPE, type);
    } else {
      url.searchParams.delete(URL_PARAM_PURCHASE_TYPE);
    }
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState({}, '', next);
    }
  }

  function removePurchaseTypeFromUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has(URL_PARAM_PURCHASE_TYPE)) return;

    url.searchParams.delete(URL_PARAM_PURCHASE_TYPE);
    const next = `${url.pathname}${url.search}${url.hash}`;
    if (next !== `${window.location.pathname}${window.location.search}${window.location.hash}`) {
      window.history.replaceState({}, '', next);
    }
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getPurchaseTypeWidgetRoot() {
    const el = document.querySelector(SELECTOR.purchaseTypeWidget);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @param {'subscription' | 'onetime'} pt
   */
  function syncPurchaseTypeRadiosFromActive(pt) {
    const root = getPurchaseTypeWidgetRoot();
    if (!root || (pt !== PURCHASE_SUBSCRIPTION && pt !== PURCHASE_ONETIME)) return;
    const input = root.querySelector(`input[type="radio"][name="${PURCHASE_TYPE_RADIO_NAME}"][value="${pt}"]`);
    if (input instanceof HTMLInputElement) input.checked = true;
  }

  function syncCtasPurchaseTypeAttrFromWidget() {
    const root = getPurchaseTypeWidgetRoot();
    if (!root) return;
    const scope = root.closest('product-info') || root.closest('.shopify-section') || document;
    scope.querySelectorAll('[data-nw-new-bundle-cta]').forEach((btn) => {
      if (btn instanceof HTMLElement) btn.setAttribute('data-purchase-type', activePurchaseType);
    });
  }

  /**
   * @param {string} moneyFormatted
   * @returns {string}
   */
  function formatMoneyWithEaSuffix(moneyFormatted) {
    const t = String(moneyFormatted ?? '').trim();
    if (!t) return '';
    if (/\/?\s*ea\b/i.test(t)) return t;
    return `${t}/ea`;
  }

  function syncPurchaseWidgetPricesFromBundleRadio() {
    const root = getPurchaseTypeWidgetRoot();
    if (!root) return;
    const radio = getActiveBundleRadio();
    if (!radio) return;

    const each = (radio.getAttribute(DATA_ATTR.eachPrice) ?? '').trim();
    const eachCmp = (radio.getAttribute(DATA_ATTR.eachComparePrice) ?? '').trim();

    const subCompareEl = root.querySelector('[data-nw-purchase-sub-compare]');
    const subEachEl = root.querySelector('[data-nw-purchase-sub-each]');
    const otEachEl = root.querySelector('[data-nw-purchase-onetime-each]');

    const showEachCompare = Boolean(eachCmp && each !== eachCmp);

    if (subCompareEl instanceof HTMLElement) {
      if (showEachCompare) {
        subCompareEl.textContent = eachCmp;
        subCompareEl.classList.remove('hidden');
        subCompareEl.hidden = false;
      } else {
        subCompareEl.textContent = '';
        subCompareEl.classList.add('hidden');
        subCompareEl.hidden = true;
      }
    }

    if (subEachEl) subEachEl.textContent = each ? formatMoneyWithEaSuffix(each) : '';
    /* One-time column: show compare-at each as regular (non-strike) copy in the span */
    if (otEachEl) otEachEl.textContent = eachCmp ? formatMoneyWithEaSuffix(eachCmp) : '';
  }

  function applyPurchaseTypeFromUrl() {
    const fromUrl = readPurchaseTypeFromUrl();
    const ptRoot = getPurchaseTypeWidgetRoot();

    if (fromUrl) {
      activePurchaseType = fromUrl;
      if (ptRoot) syncPurchaseTypeRadiosFromActive(fromUrl);
    } else if (ptRoot) {
      const checked = ptRoot.querySelector(`input[name="${PURCHASE_TYPE_RADIO_NAME}"]:checked`);
      const parsed =
        checked instanceof HTMLInputElement ? normalizePurchaseType(checked.value) : null;
      activePurchaseType = parsed ?? PURCHASE_SUBSCRIPTION;
    } else {
      activePurchaseType = PURCHASE_SUBSCRIPTION;
    }

    if (ptRoot) {
      syncCtasPurchaseTypeAttrFromWidget();
      syncPurchaseWidgetPricesFromBundleRadio();
    }

    syncFlavorsModalPurchaseUi();
  }

  function syncPurchaseTypeUrl() {
    if (readPurchaseTypeFromUrl() === activePurchaseType) return;
    writePurchaseTypeToUrl(activePurchaseType);
  }

  /**
   * @returns {'subscription' | 'onetime'}
   */
  function getActivePurchaseType() {
    return activePurchaseType;
  }

  // ---------------------------------------------------------------------------
  // DOM: gift cards
  // ---------------------------------------------------------------------------

  /**
   * @returns {HTMLInputElement | null}
   */
  function getActiveBundleRadio() {
    const checked = document.querySelector(`${SELECTOR.bundleRadio}:checked`);
    if (checked instanceof HTMLInputElement) return checked;
    const first = document.querySelector(SELECTOR.bundleRadio);
    return first instanceof HTMLInputElement ? first : null;
  }

  /**
   * Subscription discount % on the active `.nw-new-bundle-option` (radio attr, else option wrapper).
   * @returns {number}
   */
  function getActiveOptionSubscriptionDiscountPercent() {
    const radio = getActiveBundleRadio();
    if (!radio) return 0;
    const option = radio.closest('.nw-new-bundle-option');
    const raw = radio.getAttribute(DATA_ATTR.subscriptionDiscountPercent)
      || (option instanceof HTMLElement ? option.getAttribute(DATA_ATTR.subscriptionDiscountPercent) : null);
    return Math.min(100, parsePositiveFloat(raw));
  }

  /**
   * Syncs `.percentage-info-container__percentage` with the active option's
   * `data-subscription-discount-percent` (number only; "%" lives in post-text).
   */
  function syncPercentageInfoWithBundle() {
    const nodes = document.querySelectorAll(SELECTOR.percentageInfo);
    if (!nodes.length) return;

    const percent = getActiveOptionSubscriptionDiscountPercent();
    const label = percent > 0 ? String(percent) : '';

    nodes.forEach((node) => {
      node.textContent = label;
    });
  }

  /**
   * Syncs `[data-nw-new-bundle-cta]` price from checked `nw-new-bundle-option` (`data-price`, `data-compare-price`).
   * Without `.nw-new-bundle-purchase-type`: tier primary + strike when compare ≠ price.
   * With `.nw-new-bundle-purchase-type`: one line only — subscription = `data-price`, one-time = `data-compare-price` (fallback `data-price` if blank); no strike `<s>`.
   */
  function syncCtaWithBundle() {
    const buttons = document.querySelectorAll(SELECTOR.ctaButton);
    if (!buttons.length) return;

    const radio = getActiveBundleRadio();
    if (!radio) return;

    const tierPrice = (radio.getAttribute(DATA_ATTR.price) ?? '').trim();
    const tierCompareRaw = (radio.getAttribute(DATA_ATTR.comparePrice) ?? '').trim();
    const showTierStrike = Boolean(tierCompareRaw && tierCompareRaw !== tierPrice);

    const ptRoot = getPurchaseTypeWidgetRoot();
    /** @type {string} */
    let primary = '';
    let hideStrike = false;

    if (ptRoot) {
      hideStrike = true;
      if (activePurchaseType === PURCHASE_SUBSCRIPTION) {
        primary = tierPrice;
      } else {
        primary = tierCompareRaw || tierPrice;
      }
    }

    buttons.forEach((btn) => {
      const currentEl = btn.querySelector(SELECTOR.ctaPriceCurrent);
      const compareEl = btn.querySelector(SELECTOR.ctaPriceCompare);
      if (!currentEl || !compareEl) return;

      if (hideStrike) {
        currentEl.textContent = primary;
        compareEl.textContent = '';
        compareEl.hidden = true;
      } else {
        currentEl.textContent = tierPrice;
        compareEl.textContent = showTierStrike ? tierCompareRaw : '';
        compareEl.hidden = !showTierStrike;
      }
      if (compareEl instanceof HTMLElement) {
        compareEl.classList.toggle('hidden', hideStrike ? true : !showTierStrike);
      }
    });
  }

  // ---------------------------------------------------------------------------
  // Flavors modal (popup)
  // ---------------------------------------------------------------------------

  /** @type {HTMLElement | null} */
  let flavorsModalLastFocus = null;
  /** Consumed once in resetAndPrimeFlavorsModal; does not mutate bundle radios. */
  let flavorsModalPrimeItemCountHint = null;

  /**
   * @returns {HTMLElement | null}
   */
  function getFlavorsModal() {
    const el = document.querySelector(SELECTOR.flavorsModal);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * "Order by" line: same rule as Liquid (now + N×86400s) but formatted in the visitor's local calendar.
   */
  /**
   * @param {Element | null | undefined} el
   * @param {string} name
   * @returns {number}
   */
  function parseCentsAttr(el, name) {
    const v = parseInt(String(el?.getAttribute(name) ?? '0'), 10);
    return Number.isFinite(v) && v >= 0 ? v : 0;
  }

  /**
   * Build a string matching Liquid `| money` using `shop.money_format` from the dialog.
   * @param {string} moneyFormat
   * @param {number} cents
   * @returns {string | null} null if format is missing or not recognized
   */
  function formatCentsWithShopMoneyFormat(moneyFormat, cents) {
    if (!moneyFormat) return null;
    const c = Math.max(0, Math.round(Number(cents)));
    const n = c / 100;
    if (!Number.isFinite(n)) return null;
    const amount2 = n.toFixed(2);
    const noDecimals = String(Math.round(n));
    const withComma = n.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    if (!/\{\{[\s]*amount/.test(moneyFormat)) return null;
    let out = moneyFormat;
    out = out.replace(/\{\{[\s]*amount_no_decimals[\s]*\}\}/g, noDecimals);
    out = out.replace(/\{\{[\s]*amount_with_comma_separator[\s]*\}\}/g, withComma);
    out = out.replace(/\{\{[\s]*amount_no_decimals_with_comma_separator[\s]*\}\}/g, Math.round(n).toLocaleString('en-US'));
    out = out.replace(/\{\{[\s]*amount[\s]*\}\}/g, amount2);
    if (out === moneyFormat) return null;
    return out;
  }

  /**
   * @param {HTMLElement} modal
   * @param {number} cents
   * @returns {string}
   */
  function formatFlavorMoneyCents(modal, cents) {
    const dialog = modal.querySelector('.nw-new-bundle-flavors-modal__dialog');
    const currency = String(dialog?.getAttribute('data-shop-currency') ?? 'USD').trim() || 'USD';
    const moneyFormat = String(dialog?.getAttribute('data-money-format') ?? '').trim();
    const n = Number(cents) / 100;

    const fromShop = formatCentsWithShopMoneyFormat(moneyFormat, cents);
    if (fromShop) return fromShop;

    try {
      let out = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
      if (String(currency).toUpperCase() === 'USD' && out.startsWith('US$')) {
        out = out.replace(/^US\$/i, '$');
      }
      return out;
    } catch {
      return `${n.toFixed(2)} ${currency}`;
    }
  }

  /**
   * Map of flavors variant ID → `{ [sellingPlanId]: priceCents }` from Liquid (subscriptions only).
   * When the bundle radio exposes `data-flavors-selling-plan-id`, we pick that plan's cents if present,
   * else fall back to `data-flavor-default-*` on each input (first allocation).
   *
   * @param {HTMLElement} modal
   * @returns {Record<string, Record<string, number>>}
   */
  function parseFlavorPlanPricingTable(modal) {
    const script = modal.querySelector('script[type="application/json"][data-nw-flavor-plan-pricing]');
    if (!script || !(script instanceof HTMLScriptElement) || !String(script.textContent || '').trim()) {
      return {};
    }
    try {
      const j = JSON.parse(String(script.textContent));
      if (!j || typeof j !== 'object' || Array.isArray(j)) return {};
      return /** @type {Record<string, Record<string, number>>} */ (j);
    } catch {
      return {};
    }
  }

  /**
   * @param {Record<string, Record<string, number>> | null | undefined} pricingByFlavor
   * @param {string} flavorId
   * @param {string[]} planKeys
   * @returns {number | null}
   */
  function getSellingPlanAllocationPriceCents(pricingByFlavor, flavorId, planKeys) {
    const plansObj =
      (pricingByFlavor && pricingByFlavor[flavorId]) ||
      (pricingByFlavor && pricingByFlavor[String(flavorId)]) ||
      {};
    if (!plansObj || typeof plansObj !== 'object') return null;
    for (let i = 0; i < planKeys.length; i += 1) {
      const k = planKeys[i];
      if (k == null || String(k).length === 0) continue;
      const sk = String(k);
      const raw = plansObj[sk];
      if (raw == null && raw !== 0) continue;
      const n = typeof raw === 'number' ? raw : Number(raw);
      if (Number.isFinite(n) && n >= 0) return Math.round(n);
    }
    return null;
  }

  /**
   * @param {HTMLElement} modal
   * @param {HTMLInputElement} input
   */
  function updateFlavorRowSubscriptionMarkup(modal, input) {
    if (!(input instanceof HTMLInputElement)) return;
    const row = input.closest('.nw-new-bundle-flavors-item');
    if (!(row instanceof HTMLElement)) return;
    const wrap = row.querySelector('.nw-new-bundle-flavors-item-price-subscription');
    if (!(wrap instanceof HTMLElement)) return;

    const subCents = parseCentsAttr(input, 'data-flavor-sub-unit-cents');
    const baseSubCents =
      parseCentsAttr(input, 'data-flavor-base-sub-unit-cents') ||
      parseCentsAttr(input, 'data-flavor-default-sub-unit-cents');
    const regCents = parseCentsAttr(input, 'data-flavor-regular-unit-cents');
    const otCents = parseCentsAttr(input, 'data-flavor-onetime-unit-cents');
    const discountPercent = getFlavorsSubscriptionDiscountPercent(modal);

    const saleSpan = wrap.querySelector('span');
    const compareS = wrap.querySelector('s');

    if (saleSpan) {
      const show = subCents > 0 ? subCents : otCents;
      saleSpan.textContent = formatFlavorMoneyCents(modal, show);
    }
    if (compareS instanceof HTMLElement) {
      let compareCents = 0;
      if (subCents > 0) {
        if (discountPercent > 0 && baseSubCents > subCents) {
          compareCents = baseSubCents;
        } else if (regCents > subCents) {
          compareCents = regCents;
        }
      }
      if (compareCents > subCents) {
        compareS.textContent = formatFlavorMoneyCents(modal, compareCents);
        compareS.classList.remove('hidden');
        compareS.hidden = false;
        compareS.setAttribute('aria-hidden', 'false');
      } else {
        compareS.textContent = '';
        compareS.classList.add('hidden');
        compareS.hidden = true;
        compareS.setAttribute('aria-hidden', 'true');
      }
    }
  }

  /**
   * Per-tier flavors selling plan from metafield — javvy PDP only.
   * Gum / maxxing keep the default flavor plan even if the attr is present.
   * @param {HTMLInputElement | null | undefined} tierRadio
   * @returns {string}
   */
  function isJavvyPdp() {
    return Boolean(document.querySelector('.product--javvy'));
  }

  /**
   * A/B buy boxes (v3/v4/javvy/v5) tag the cart aov_test=maxxing for their free shipping
   * and tier pricing. Anywhere else a tag left over from an earlier visit must not apply.
   * @returns {boolean}
   */
  function isAbTaggedPdp() {
    return Boolean(document.querySelector('[data-nw-maxxing-gift-tiers], .nw-new-bundle-options--maxxing'));
  }

  function getJavvyFlavorsSellingPlanId(tierRadio) {
    if (!isJavvyPdp()) return '';
    const raw = tierRadio?.getAttribute('data-flavors-selling-plan-id');
    return raw != null ? String(raw).trim() : '';
  }

  /**
   * Javvy PDP radios only (2 / 4 / 6 / 8-box). Other templates keep using
   * {@link findBundleRadioByItemCount} / {@link getActiveBundleRadio}.
   * @returns {HTMLInputElement[]}
   */
  function getJavvyBundleRadios() {
    /** @type {HTMLInputElement[]} */
    const out = [];
    document.querySelectorAll(`.product--javvy input[name="${RADIO_NAME}"]`).forEach((el) => {
      if (el instanceof HTMLInputElement && el.type === 'radio') out.push(el);
    });
    return out;
  }

  /**
   * Javvy only: discount tier from **flavor-modal qty**, not the PDP radio
   * that opened the popup. 2→2-pack, 4→4-pack, 6→6-pack, 8+→8-pack.
   * 3→2, 5→4, 7→6 (highest pack ≤ qty).
   * @param {number} qty
   * @returns {HTMLInputElement | null}
   */
  function getJavvyDiscountRadioForQty(qty) {
    const n = parsePositiveInt(qty);
    /** @type {{ el: HTMLInputElement, boxes: number }[]} */
    const tiers = [];
    getJavvyBundleRadios().forEach((el) => {
      const boxes = parsePositiveInt(el.getAttribute(DATA_ATTR.itemCount));
      if (boxes > 0) tiers.push({ el, boxes });
    });
    if (!tiers.length) return null;

    if (n >= 8) {
      const eight = tiers.filter((t) => t.boxes === 8)[0];
      if (eight) return eight.el;
      const atLeast8 = tiers.filter((t) => t.boxes >= 8).sort((a, b) => a.boxes - b.boxes);
      if (atLeast8.length) return atLeast8[0].el;
      return tiers.slice().sort((a, b) => b.boxes - a.boxes)[0].el;
    }

    const exact = n > 0 ? tiers.filter((t) => t.boxes === n)[0] : null;
    if (exact) return exact.el;

    const atOrBelow = tiers.filter((t) => t.boxes <= n).sort((a, b) => b.boxes - a.boxes);
    if (atOrBelow.length) return atOrBelow[0].el;

    return tiers.slice().sort((a, b) => a.boxes - b.boxes)[0].el;
  }

  /**
   * @returns {string[]}
   */
  function getJavvyTierDiscountCodes() {
    const codes = [];
    const seen = new Set();
    getJavvyBundleRadios().forEach((el) => {
      const code = String(el.getAttribute(DATA_ATTR.discountCode) ?? '').trim();
      if (!code) return;
      const key = code.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      codes.push(code);
    });
    return codes;
  }

  /**
   * Drop leftover 35-OFF / 40-OFF / … so qty edits do not stack pack codes.
   * @param {string[] | null | undefined} preserve
   * @returns {string[]}
   */
  function stripJavvyTierDiscountCodes(preserve) {
    const pack = new Set(getJavvyTierDiscountCodes().map((c) => c.toUpperCase()));
    if (!pack.size) return Array.isArray(preserve) ? preserve.slice() : [];
    return (Array.isArray(preserve) ? preserve : []).filter((raw) => {
      const key = String(raw ?? '').trim().toUpperCase();
      return key && !pack.has(key);
    });
  }

  /**
   * Sync flavor line items with the bundle tier that matches **current total flavor qty** (sum of line
   * quantities) when that total is &gt; 0, so per-row subscription price + cart `selling_plan` follow +/- changes.
   * When total is 0, uses the active PDP bundle radio. Javvy: optional `data-flavors-selling-plan-id` on the tier
   * radio; else defaults from Liquid (`data-flavor-default-*`).
   *
   * @param {HTMLElement} modal
   */
  function syncFlavorInputsWithBundleSellingPlan(modal) {
    if (!(modal instanceof HTMLElement)) return;

    const selection = getFlavorsSelectionState(modal);
    const totalFlavorQty = selection.totalQty;

    const tierRadio =
      totalFlavorQty > 0
        ? getBundleRadioForFlavorTotalQty(totalFlavorQty)
        : getActiveBundleRadio();

    const trimmed = getJavvyFlavorsSellingPlanId(tierRadio);
    const customPlanNumeric = trimmed ? parsePositiveInt(trimmed) : 0;
    /** @type {string[]} */
    const planKeyCandidates = [];
    if (trimmed) planKeyCandidates.push(trimmed);
    if (customPlanNumeric > 0 && String(customPlanNumeric) !== trimmed) planKeyCandidates.push(String(customPlanNumeric));

    const pricingTable = trimmed ? parseFlavorPlanPricingTable(modal) : {};
    const discountPercent = getFlavorsSubscriptionDiscountPercent(modal);

    modal.querySelectorAll('[data-flavor-input]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;

      const fid = String(el.getAttribute('data-flavor-id') ?? '').trim();
      if (!fid) return;

      const defaultPlanNumeric = parsePositiveInt(String(el.getAttribute('data-flavor-default-selling-plan-id') ?? '').trim());
      const defaultSub = parseCentsAttr(el, 'data-flavor-default-sub-unit-cents');

      /** @type {number} */
      let resolvedPlanNumeric = defaultPlanNumeric;
      /** @type {number} */
      let resolvedSubCents = defaultSub;
      let usedCustomPlan = false;

      if (trimmed && planKeyCandidates.length) {
        const found = getSellingPlanAllocationPriceCents(pricingTable, fid, planKeyCandidates);
        if (found != null && customPlanNumeric > 0) {
          resolvedPlanNumeric = customPlanNumeric;
          resolvedSubCents = found;
          usedCustomPlan = true;
        }
      }

      let baseSubCents = resolvedSubCents;
      if (!usedCustomPlan && discountPercent > 0) {
        const regCents = parseCentsAttr(el, 'data-flavor-regular-unit-cents');
        if (regCents > 0) baseSubCents = regCents;
        resolvedSubCents = applySubscriptionDiscountPercent(baseSubCents, discountPercent);
      }

      if (resolvedPlanNumeric > 0) {
        el.setAttribute('data-flavor-selling-plan-id', String(resolvedPlanNumeric));
      } else {
        el.removeAttribute('data-flavor-selling-plan-id');
      }

      el.setAttribute('data-flavor-base-sub-unit-cents', String(baseSubCents));
      el.setAttribute('data-flavor-sub-unit-cents', String(resolvedSubCents));

      if (resolvedSubCents > 0) {
        el.setAttribute('data-flavor-selling-plan-price', formatFlavorMoneyCents(modal, resolvedSubCents));
      } else {
        el.removeAttribute('data-flavor-selling-plan-price');
      }

      updateFlavorRowSubscriptionMarkup(modal, el);
    });
  }

  /**
   * Flavors modal footer: per-line totals, subtotal, save, shipping row, gift count, CTA prices.
   * @param {HTMLElement} modal
   */
  function updateFlavorsFooterSummary(modal) {
    const isSub = activePurchaseType === PURCHASE_SUBSCRIPTION;

    const selection = getFlavorsSelectionState(modal);
    const totalBoxes = selection.totalQty;

    /** @type {Map<string, HTMLInputElement>} */
    const inputsById = new Map();
    modal.querySelectorAll('[data-flavor-input]').forEach((el) => {
      if (el instanceof HTMLInputElement) {
        const id = String(el.getAttribute('data-flavor-id') ?? '').trim();
        if (id) inputsById.set(id, el);
      }
    });

    let sumSale = 0;
    let sumCompare = 0;

    modal.querySelectorAll('[data-flavor-summary-row]').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const fid = String(row.getAttribute('data-summary-flavor-id') ?? '').trim();
      const input = inputsById.get(fid);
      const qty = input ? parsePositiveInt(input.value) : 0;

      const qtySpan = row.querySelector('[data-flavor-quantity]');
      const compareEl = row.querySelector('[data-flavor-line-compare]');
      const saleEl = row.querySelector('[data-flavor-line-sale]');

      if (qty <= 0) {
        row.classList.add('hidden');
        return;
      }

      row.classList.remove('hidden');
      if (qtySpan) qtySpan.textContent = ` X ${qty}`;

      let unitSale = 0;
      let unitCompare = 0;
      if (input) {
        const subCents = parseCentsAttr(input, 'data-flavor-sub-unit-cents');
        const otCents = parseCentsAttr(input, 'data-flavor-onetime-unit-cents');
        const regCents = parseCentsAttr(input, 'data-flavor-regular-unit-cents');
        const compareAtCents = parseCentsAttr(input, 'data-flavor-compare-at-cents');

        if (isSub && subCents > 0) {
          unitSale = subCents;
          const baseSubCents =
            parseCentsAttr(input, 'data-flavor-base-sub-unit-cents') ||
            parseCentsAttr(input, 'data-flavor-default-sub-unit-cents');
          const discountPercent = getFlavorsSubscriptionDiscountPercent(modal);
          if (discountPercent > 0 && baseSubCents > subCents) {
            unitCompare = baseSubCents;
          } else {
            unitCompare = regCents > 0 ? regCents : otCents;
          }
        } else {
          unitSale = otCents;
          unitCompare = compareAtCents > 0 ? compareAtCents : otCents;
        }
      }

      const lineSale = qty * unitSale;
      const lineCompare = qty * unitCompare;
      sumSale += lineSale;
      sumCompare += lineCompare;

      if (saleEl) saleEl.textContent = formatFlavorMoneyCents(modal, lineSale);
      if (compareEl instanceof HTMLElement) {
        if (!isSub) {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        } else if (lineCompare > lineSale) {
          compareEl.textContent = formatFlavorMoneyCents(modal, lineCompare);
          compareEl.classList.remove('hidden');
        } else {
          compareEl.textContent = '';
          compareEl.classList.add('hidden');
        }
      }
    });

    const subtotalCompareEl = modal.querySelector('[data-flavor-subtotal-compare]');
    const subtotalSaleEl = modal.querySelector('[data-flavor-subtotal-sale]');
    if (subtotalSaleEl instanceof HTMLElement) {
      subtotalSaleEl.textContent = formatFlavorMoneyCents(modal, sumSale);
    }
    if (subtotalCompareEl instanceof HTMLElement) {
      if (!isSub) {
        subtotalCompareEl.textContent = '';
        subtotalCompareEl.classList.add('hidden');
      } else if (sumCompare > sumSale) {
        subtotalCompareEl.textContent = formatFlavorMoneyCents(modal, sumCompare);
        subtotalCompareEl.classList.remove('hidden');
      } else {
        subtotalCompareEl.textContent = '';
        subtotalCompareEl.classList.add('hidden');
      }
    }

    const saveEl = modal.querySelector('[data-flavor-summary-save-amount]');
    const footerSave = modal.querySelector('[data-flavor-footer-save-tag]');
    if (isSub) {
      const saveCents = Math.max(0, sumCompare - sumSale);
      const saveFormatted = formatFlavorMoneyCents(modal, saveCents);
      if (saveEl) saveEl.textContent = saveFormatted;
      if (footerSave) {
        const dialog = modal.querySelector('.nw-new-bundle-flavors-modal__dialog');
        const savePrefix = String(dialog?.getAttribute('data-flavor-save-prefix') ?? 'Save ');
        footerSave.textContent = saveCents > 0 ? `${savePrefix} ${saveFormatted}` : '';
      }
    } else {
      if (saveEl) saveEl.textContent = '';
      if (footerSave) footerSave.textContent = '';
    }

    modal.querySelector('[data-nw-flavor-header-promo]')?.classList.toggle('hidden', !isSub);
    modal.querySelector('[data-nw-flavor-summary-save-row]')?.classList.toggle('hidden', !isSub);
    modal.querySelector('[data-nw-flavor-footer-save-tag-wrap]')?.classList.toggle('hidden', !isSub);

    modal.querySelectorAll('[data-total-price]').forEach((el) => {
      el.textContent = formatFlavorMoneyCents(modal, sumSale);
    });
    modal.querySelectorAll('[data-total-price-compare]').forEach((el) => {
      if (isSub && sumCompare > sumSale) {
        el.textContent = formatFlavorMoneyCents(modal, sumCompare);
        el.classList.remove('hidden');
      } else {
        el.textContent = '';
        el.classList.add('hidden');
      }
    });
    const totalCount = modal.querySelector('[data-total-count]');
    if (totalCount) totalCount.textContent = String(totalBoxes);

    const unlockedGifts = modal.querySelector('[data-flavor-unlocked-gifts-count]');
    if (unlockedGifts) {
      const slots = parseFlavorTierSlots(modal).filter((s) => s.min > 0 || s.minCents > 0);
      const n = slots.filter((s) => isFlavorTierUnlocked(s, totalBoxes, sumSale)).length;
      unlockedGifts.textContent = String(n);
    }

    const shipRow = modal.querySelector('[data-flavor-summary-shipping-row]');
    if (shipRow instanceof HTMLElement) {
      const fsSlots = parseFlavorTierSlots(modal).filter((s) => s.freeShipping);
      const showShip = fsSlots.some((s) => isFlavorTierUnlocked(s, totalBoxes, sumSale));
      shipRow.classList.toggle('hidden', !showShip);
    }
  }

  function syncFlavorsOrderDeadlineDates() {
    document.querySelectorAll('[data-nw-order-deadline]').forEach((root) => {
      if (!(root instanceof HTMLElement)) return;
      const daysRaw = root.getAttribute('data-nw-order-deadline-days');
      const days = parsePositiveInt(daysRaw);
      const target = root.querySelector('[data-nw-order-deadline-date]');
      if (!(target instanceof HTMLElement)) return;
      const deadline = new Date(Date.now() + days * 86400000);
      try {
        target.textContent = deadline.toLocaleDateString(undefined, {
          month: 'long',
          day: 'numeric',
          year: 'numeric',
        });
      } catch {
        target.textContent = deadline.toISOString().slice(0, 10);
      }
    });
  }

  /**
   * @typedef {{ flavorId: string, title: string, qty: number, input: HTMLInputElement, row: HTMLElement | null }} NwFlavorLine
   * @typedef {{ lines: NwFlavorLine[], totalQty: number, distinctWithQty: number, qtyByFlavorId: Map<string, number> }} NwFlavorsSelection
   */

  /**
   * Single snapshot of flavor quantities (for tier copy, CTA, qty controls).
   * @param {HTMLElement} modal
   * @returns {NwFlavorsSelection}
   */
  function getFlavorsSelectionState(modal) {
    /** @type {NwFlavorLine[]} */
    const lines = [];
    const qtyByFlavorId = new Map();

    modal.querySelectorAll('[data-flavor-input]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const id = String(el.getAttribute('data-flavor-id') ?? '').trim();
      const title = String(el.getAttribute('data-flavor-title') ?? '').trim();
      const qty = parsePositiveInt(el.value);
      const row = el.closest('.nw-new-bundle-flavors-item');
      lines.push({
        flavorId: id,
        title,
        qty,
        input: el,
        row: row instanceof HTMLElement ? row : null,
      });
      if (id) qtyByFlavorId.set(id, qty);
    });

    let totalQty = 0;
    let distinctWithQty = 0;
    for (let i = 0; i < lines.length; i += 1) {
      totalQty += lines[i].qty;
      if (lines[i].qty > 0) distinctWithQty += 1;
    }

    return { lines, totalQty, distinctWithQty, qtyByFlavorId };
  }

  /**
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function getFlavorInputsTotal(modal) {
    return getFlavorsSelectionState(modal).totalQty;
  }

  /**
   * Sale-price total (cents) of the current flavor selection — used to unlock
   * dollar-threshold tiers (e.g. Free Shipping) instead of box-count tiers.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function computeFlavorsSelectionCents(modal) {
    const isSub = activePurchaseType === PURCHASE_SUBSCRIPTION;
    let sumSale = 0;
    modal.querySelectorAll('[data-flavor-input]').forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const qty = parsePositiveInt(input.value);
      if (qty <= 0) return;
      const subCents = parseCentsAttr(input, 'data-flavor-sub-unit-cents');
      const otCents = parseCentsAttr(input, 'data-flavor-onetime-unit-cents');
      const unitSale = isSub && subCents > 0 ? subCents : otCents;
      sumSale += qty * unitSale;
    });
    return sumSale;
  }

  /**
   * @param {{ min: number, minCents: number, freeShipping: boolean }} slot
   * @param {number} totalBoxes
   * @param {number} cents
   * @returns {boolean}
   */
  function isFlavorTierUnlocked(slot, totalBoxes, cents) {
    if (slot.freeShipping) return slot.minCents > 0 && cents >= slot.minCents;
    return slot.min > 0 && totalBoxes >= slot.min;
  }

  /**
   * How many flavor rows have quantity greater than zero.
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function countFlavorsWithPositiveQty(modal) {
    return getFlavorsSelectionState(modal).distinctWithQty;
  }

  /**
   * Minimum total box count from `data-min-flavor-count` (sum of all flavor line qtys).
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function getMinTotalBoxesFromModal(modal) {
    const list = modal.querySelector('[data-min-flavor-count]');
    const raw = list?.getAttribute('data-min-flavor-count');
    const n = parsePositiveInt(raw);
    return n > 0 ? n : 2;
  }

  /**
   * @param {HTMLElement} row
   */
  function expandFlavorQuantityRow(row) {
    const addWrap = row.querySelector('.nw-new-bundle-flavors-item-action-button');
    const qtyWrap = row.querySelector('.nw-new-bundle-flavors-item-action-quantity');
    if (addWrap) addWrap.classList.add('hidden');
    if (qtyWrap) qtyWrap.classList.remove('hidden');
  }

  /**
   * @param {HTMLElement} row
   */
  function collapseFlavorQuantityRow(row) {
    const addWrap = row.querySelector('.nw-new-bundle-flavors-item-action-button');
    const qtyWrap = row.querySelector('.nw-new-bundle-flavors-item-action-quantity');
    const input = row.querySelector('[data-flavor-input]');
    if (input instanceof HTMLInputElement) input.value = '0';
    if (addWrap) addWrap.classList.remove('hidden');
    if (qtyWrap) qtyWrap.classList.add('hidden');
  }

  /**
   * Spread total boxes across the first flavor rows in chunks of 2
   * (e.g. 8 → four rows at 2; 6 → three at 2; leftover goes on the last used row).
   * @param {HTMLElement} list
   * @param {number} targetTotal
   */
  function primeFlavorsModalQuantities(list, targetTotal) {
    if (!(list instanceof HTMLElement) || targetTotal <= 0) return;

    const rows = [...list.querySelectorAll('.nw-new-bundle-flavors-item:not(.nw-new-bundle-flavors-item--sold-out)')].filter(
      (row) => row instanceof HTMLElement,
    );
    if (!rows.length) return;

    const perFlavor = 2;
    let remaining = targetTotal;
    let lastPrimedIdx = -1;

    for (let i = 0; i < rows.length && remaining > 0; i += 1) {
      const qty = Math.min(perFlavor, remaining);
      const row = rows[i];
      expandFlavorQuantityRow(row);
      const input = row.querySelector('[data-flavor-input]');
      if (input instanceof HTMLInputElement) input.value = String(qty);
      remaining -= qty;
      lastPrimedIdx = i;
    }

    if (remaining > 0 && lastPrimedIdx >= 0) {
      const lastRow = rows[lastPrimedIdx];
      const input = lastRow.querySelector('[data-flavor-input]');
      if (input instanceof HTMLInputElement) {
        input.value = String(parsePositiveInt(input.value) + remaining);
      }
    }
  }

  function resetAndPrimeFlavorsModal() {
    const modal = getFlavorsModal();
    if (!modal) return;
    const list = modal.querySelector('[data-min-flavor-count]');
    if (!list) return;

    modal.querySelectorAll('.nw-new-bundle-flavors-item').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      collapseFlavorQuantityRow(row);
    });

    const hinted = flavorsModalPrimeItemCountHint;
    flavorsModalPrimeItemCountHint = null;
    // Cart-drawer upsell: always start from zero, regardless of whatever
    // bundle-tier radio happens to be selected on the underlying PDP —
    // this is a fresh add-on pick, not a continuation of that selection.
    const target = window.__nwUpsellFlavorsFlow
      ? 0
      : (hinted != null && hinted > 0 ? hinted : getSelectedBundleItemCount());
    primeFlavorsModalQuantities(list, target);

    refreshFlavorsModalState();
  }

  /**
   * @param {string} s
   * @returns {string}
   */
  function escapeHtmlText(s) {
    return String(s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  /**
   * @param {string[]} escapedParts HTML-escaped fragments
   * @returns {string}
   */
  function formatGiftNamesListHtml(escapedParts) {
    const parts = escapedParts.filter(Boolean);
    if (parts.length === 0) return '';
    if (parts.length === 1) return parts[0];
    if (parts.length === 2) return `${parts[0]} and ${parts[1]}`;
    return `${parts.slice(0, -1).join(', ')}, and ${parts[parts.length - 1]}`;
  }

  /**
   * @param {HTMLElement} modal
   * @returns {{ min: number, slot: number, label: string }[]}
   */
  /**
   * @param {HTMLElement} modal
   * @returns {{ min: number, minCents: number, slot: number, label: string, freeShipping: boolean }[]}
   */
  function parseFlavorTierSlots(modal) {
    return [...modal.querySelectorAll('li[data-tier-min-boxes]')]
      .map((el) => {
        const min = parsePositiveInt(el.getAttribute('data-tier-min-boxes'));
        const minCents = parsePositiveInt(el.getAttribute('data-tier-min-cents'));
        const slot = parsePositiveInt(el.getAttribute('data-tier-slot'));
        const labelEl = el.querySelector('.nw-new-bundle-flavors-tier__label');
        const label = (labelEl?.textContent || '').replace(/\s+/g, ' ').trim();
        const freeShipping = el.getAttribute('data-tier-free-shipping') === 'true';
        return { min, minCents, slot: slot || 0, label, freeShipping };
      })
      .sort((a, b) => a.slot - b.slot || a.min - b.min);
  }

  /**
   * @param {{ label: string, freeShipping: boolean }[]} unlockedSlots
   * @returns {string} plain text for heading (gift icon row)
   */
  function formatUnlockedTierHeading(unlockedSlots) {
    if (!unlockedSlots.length) return '';
    // Tiers that unlock at the same box count show as "A + B unlocked!".
    // Free shipping is always its own headline — never "Gift + … + Free shipping unlocked!".
    if (unlockedSlots.some((s) => s.freeShipping)) {
      return 'Free shipping unlocked!';
    }
    const labels = unlockedSlots.map((s) => s.label).filter(Boolean);
    if (!labels.length) return 'Rewards unlocked!';
    if (labels.length === 1) return `${labels[0]} unlocked!`;
    return `${labels.join(' + ')} unlocked!`;
  }

  /**
   * @param {HTMLElement} modal
   * @returns {boolean}
   */
  function isV3FlavorsModal(modal) {
    return modal.classList.contains('nw-new-bundle-flavors-modal--v3');
  }

  /**
   * @param {HTMLElement} modal
   * @returns {boolean}
   */
  function isV4FlavorsModal(modal) {
    return modal.classList.contains('nw-new-bundle-flavors-modal--v4');
  }

  /**
   * Shipping stays on the v4 track and unlocks with the first gift (2 boxes).
   * It is not a counted gift in the heading or the progress line.
   * @param {{ min: number, minCents: number, freeShipping: boolean }} slot
   * @param {number} totalBoxes
   * @param {number} cents
   * @returns {boolean}
   */
  function isV4FlavorTierUnlocked(slot, totalBoxes, cents) {
    if (slot.freeShipping) return slot.min > 0 && totalBoxes >= slot.min;
    return isFlavorTierUnlocked(slot, totalBoxes, cents);
  }

  /**
   * @param {number} count
   * @returns {string}
   */
  function v3UnlockedGiftsHeading(count) {
    return count === 1 ? '1 Free Gift Unlocked' : `${count} Free Gifts Unlocked`;
  }

  /**
   * How many still-locked steps share the next threshold (same box count,
   * or the same shipping dollar threshold).
   * @param {{ min: number, minCents: number, freeShipping: boolean }[]} slots
   * @param {number} total
   * @param {number} cents
   * @returns {number}
   */
  function v3NextTierGiftCount(slots, total, cents) {
    const locked = slots.filter((s) => !isFlavorTierUnlocked(s, total, cents));
    if (!locked.length) return 0;
    const next = locked[0];
    if (next.freeShipping) {
      return locked.filter((s) => s.freeShipping && s.minCents === next.minCents).length;
    }
    return locked.filter((s) => s.min > 0 && s.min === next.min).length;
  }

  /**
   * Tier copy: heading (partial unlock), complete + list (all unlock), progress line.
   * @param {HTMLElement} modal
   * @param {number} total
   * @param {number} cents
   */
  function syncFlavorTierMessagePanel(modal, total, cents) {
    const msgEl = modal.querySelector('[data-flavors-tier-message]');
    const headerBox = modal.querySelector('[data-nw-flavors-tier-header-box]');
    const headingWrap = modal.querySelector('[data-flavors-tier-heading-wrap]');
    const headingTextEl = modal.querySelector('[data-flavors-tier-heading-text]');
    const completeWrap = modal.querySelector('[data-flavors-tier-complete]');
    const listWrap = modal.querySelector('[data-flavors-tier-all-unlocked-list]');

    if (!msgEl) return;

    const slots = parseFlavorTierSlots(modal).filter((s) => s.min > 0 || s.minCents > 0);

    if (!slots.length) {
      msgEl.textContent = 'Select flavors to see reward progress.';
      msgEl.classList.remove('hidden');
      headerBox?.classList.remove('nw-new-bundle-flavors-modal__header-tier-box--all-unlocked');
      headingWrap?.classList.add('hidden');
      if (headingTextEl) headingTextEl.textContent = '';
      completeWrap?.classList.add('hidden');
      listWrap?.classList.add('hidden');
      return;
    }

    const v4TierCopy = isV4FlavorsModal(modal);
    const tierIsUnlocked = (slot) =>
      v4TierCopy ? isV4FlavorTierUnlocked(slot, total, cents) : isFlavorTierUnlocked(slot, total, cents);
    const giftSlots = v4TierCopy ? slots.filter((s) => !s.freeShipping) : slots;
    const allUnlocked = giftSlots.every((s) => tierIsUnlocked(s));
    const unlockedGiftSlots = giftSlots.filter((s) => tierIsUnlocked(s));
    const unlockedSlots = slots.filter((s) => tierIsUnlocked(s));

    /** @type {{ min: number, minCents: number, slot: number, label: string, freeShipping: boolean } | null} */
    let next = null;
    for (let i = 0; i < giftSlots.length; i += 1) {
      if (!tierIsUnlocked(giftSlots[i])) {
        next = giftSlots[i];
        break;
      }
    }

    headerBox?.classList.toggle('nw-new-bundle-flavors-modal__header-tier-box--all-unlocked', allUnlocked);

    const showHeading = unlockedGiftSlots.length > 0 && !allUnlocked;
    const v3TierCopy = isV3FlavorsModal(modal);
    if (headingWrap) headingWrap.classList.toggle('hidden', !showHeading);
    if (headingTextEl) {
      if (!showHeading) {
        headingTextEl.textContent = '';
      } else if (v3TierCopy || v4TierCopy) {
        headingTextEl.textContent = v3UnlockedGiftsHeading(unlockedGiftSlots.length);
      } else {
        headingTextEl.textContent = formatUnlockedTierHeading(unlockedSlots);
      }
    }

    if (completeWrap) completeWrap.classList.toggle('hidden', !allUnlocked);
    if (listWrap) listWrap.classList.toggle('hidden', !allUnlocked);

    if (allUnlocked) {
      msgEl.innerHTML = '';
      msgEl.classList.add('hidden');
      return;
    }

    msgEl.classList.remove('hidden');

    if (v4TierCopy) {
      const needBoxes = next ? next.min - total : 0;
      if (needBoxes > 0) {
        const boxPhrase =
          needBoxes === 1
            ? '<strong>1 more box</strong>'
            : `<strong>${needBoxes} more boxes</strong>`;
        msgEl.innerHTML = `Add ${boxPhrase} to unlock <strong>1 more free gift</strong>`;
        return;
      }
    }

    if (v3TierCopy) {
      const nextGiftCount = v3NextTierGiftCount(slots, total, cents);
      const giftPhrase =
        nextGiftCount === 1
          ? '<strong>1 more free gift</strong>'
          : `<strong>${nextGiftCount} more free gifts</strong>`;
      if (next && next.freeShipping) {
        const needCents = Math.max(0, next.minCents - cents);
        const needMoney = formatFlavorMoneyCents(modal, needCents);
        msgEl.innerHTML = `Add <strong>${needMoney}</strong> more to unlock ${giftPhrase}`;
        return;
      }
      const needBoxes = next ? next.min - total : 0;
      if (needBoxes > 0 && nextGiftCount > 0) {
        const boxPhrase =
          needBoxes === 1
            ? '<strong>1 more box</strong>'
            : `<strong>${needBoxes} more boxes</strong>`;
        msgEl.innerHTML = `Add ${boxPhrase} to unlock ${giftPhrase}`;
        return;
      }
    }

    const nextEsc = next ? escapeHtmlText(next.label) : '';

    if (next && next.freeShipping) {
      const needCents = Math.max(0, next.minCents - cents);
      const needMoney = formatFlavorMoneyCents(modal, needCents);
      msgEl.innerHTML = `Add <strong>${needMoney}</strong> more for <strong>${nextEsc}</strong>`;
      return;
    }

    const need = next ? next.min - total : 0;

    if (unlockedSlots.length === 0 && need > 0) {
      const boxPhrase =
        need === 1
          ? '<strong>1 more box</strong>'
          : `<strong>${need} more boxes</strong>`;
      msgEl.innerHTML = `Add ${boxPhrase} to unlock rewards`;
      return;
    }

    if (next && need > 0) {
      const morePhrase = need === 1 ? '<strong>1 more</strong>' : `<strong>${need} more</strong>`;
      msgEl.innerHTML = `Add ${morePhrase} for <strong>${nextEsc}</strong>`;
      return;
    }

    msgEl.textContent = 'Keep adding boxes to unlock more rewards.';
  }

  /**
   * Theme: only require at least one box for checkout (ignore minimum total box count).
   * @param {HTMLElement} modal
   * @returns {boolean}
   */
  function flavorsLenientCheckout(modal) {
    const list = modal.querySelector('[data-min-flavor-count]');
    return list?.getAttribute('data-flavors-lenient-checkout') === 'true';
  }

  /**
   * CTA: lenient → any totalQty ≥ 1; otherwise totalQty must meet minimum box count (all flavors summed).
   * Cart-drawer upsell always enforces the full minimum regardless of the
   * PDP's lenient-checkout setting — it's specifically a multi-box add-on,
   * not the PDP's "buy just 1 box" flow.
   * @param {HTMLElement} modal
   * @param {NwFlavorsSelection} [selection] from {@link getFlavorsSelectionState}; omit to compute once.
   * @returns {boolean}
   */
  function flavorsSelectionMeetsRequirements(modal, selection) {
    const s = selection ?? getFlavorsSelectionState(modal);
    const minBoxes = getMinTotalBoxesFromModal(modal);
    if (s.totalQty <= 0) return false;
    if (!window.__nwUpsellFlavorsFlow && flavorsLenientCheckout(modal)) return true;
    return s.totalQty >= minBoxes;
  }

  /**
   * Boxes still needed before checkout CTA unlocks.
   * @param {HTMLElement} modal
   * @param {NwFlavorsSelection} [selection]
   * @returns {number}
   */
  function getFlavorCtaRemainingCount(modal, selection) {
    const s = selection ?? getFlavorsSelectionState(modal);
    if (flavorsLenientCheckout(modal)) {
      return s.totalQty > 0 ? 0 : 1;
    }
    const minBoxes = getMinTotalBoxesFromModal(modal);
    return Math.max(0, minBoxes - s.totalQty);
  }

  /**
   * @param {HTMLElement} modal
   * @param {NwFlavorsSelection} [selection]
   */
  function updateMainFlavorCtaState(modal, selection) {
    const btn = modal.querySelector('[data-main-cta]');
    if (!(btn instanceof HTMLButtonElement)) return;
    const s = selection ?? getFlavorsSelectionState(modal);
    const ok = flavorsSelectionMeetsRequirements(modal, s);
    const busy = btn.classList.contains('loading');
    btn.disabled = !ok || busy;
    btn.setAttribute('aria-disabled', ok && !busy ? 'false' : 'true');
    btn.classList.toggle('is-incomplete', !ok);

    btn.querySelectorAll('[data-cta-ready-only]').forEach((el) => {
      if (el instanceof HTMLElement) el.classList.toggle('hidden', !ok);
    });

    const readyLabel = btn.querySelector('[data-cta-ready-label]');
    if (readyLabel instanceof HTMLElement) {
      readyLabel.classList.toggle('hidden', !ok);
      const label = flavorsCtaWillRedirectToCheckout()
        ? btn.getAttribute('data-cta-checkout-label')
        : btn.getAttribute('data-cta-drawer-label');
      if (label) readyLabel.textContent = label;
    }

    const needMoreLabel = btn.querySelector('[data-cta-need-more-label]');
    if (!(needMoreLabel instanceof HTMLElement)) return;

    if (ok) {
      needMoreLabel.classList.add('hidden');
      needMoreLabel.textContent = '';
      return;
    }

    const need = getFlavorCtaRemainingCount(modal, s);
    const template = String(
      btn.getAttribute('data-cta-need-more-template') || 'Add {count} more to continue'
    );
    needMoreLabel.textContent = template.replace(/\{count\}/g, String(need));
    needMoreLabel.classList.remove('hidden');
  }

  /**
   * Cart-drawer-only helper text below the CTA when fewer than the minimum
   * box count is selected (e.g. "Add at least 2 boxes" / "Add at least 1
   * more box") — the PDP's own lenient-checkout copy elsewhere covers that
   * context, so this stays hidden there.
   * @param {HTMLElement} modal
   * @param {NwFlavorsSelection} [selection]
   */
  function updateCartMinBoxesMessage(modal, selection) {
    const el = modal.querySelector('[data-nw-cart-min-boxes-message]');
    if (!el) return;

    if (!window.__nwUpsellFlavorsFlow) {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    const s = selection ?? getFlavorsSelectionState(modal);
    const minBoxes = getMinTotalBoxesFromModal(modal);
    const need = minBoxes - s.totalQty;

    if (need <= 0) {
      el.hidden = true;
      el.textContent = '';
      return;
    }

    el.hidden = false;
    if (s.totalQty === 0) {
      el.textContent = `Add at least ${minBoxes} box${minBoxes === 1 ? '' : 'es'}`;
    } else {
      el.textContent = `Add at least ${need} more box${need === 1 ? '' : 'es'}`;
    }
  }

  /**
   * Tier gift `data-exclude-from-cart` — when true, do not add that gift.
   * Also skips `data-tier-free-shipping` rows (UI-only shipping tier).
   * @param {string | null | undefined} raw
   * @returns {boolean}
   */
  /**
   * @param {string | undefined} routeUrl theme `routes.*_url` (often without `.js`)
   * @param {string} fallback
   * @returns {string}
   */
  function cartAjaxJsUrl(routeUrl, fallback) {
    const base = typeof routeUrl === 'string' && routeUrl.length ? routeUrl : fallback;
    if (base.endsWith('.js')) return base;
    return `${base.replace(/\/?$/, '')}.js`;
  }

  /**
   * @returns {string} root-relative checkout path + search
   */
  function getStoreCheckoutUrl() {
    const raw = window.routes?.cart_url;
    if (typeof raw !== 'string' || !raw.length) return '/checkout';
    try {
      const u = new URL(raw, window.location.origin);
      u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
      return `${u.pathname}${u.search}`;
    } catch {
      return '/checkout';
    }
  }

  /**
   * @returns {string} Shopify locale/root prefix, always with a trailing slash
   */
  function shopifyRootPath() {
    const raw = window.Shopify?.routes?.root;
    if (typeof raw !== 'string' || !raw.length) return '/';
    return raw.endsWith('/') ? raw : `${raw}/`;
  }

  /**
   * Checkout URL that applies codes via /checkout?discount=CODE — more reliable
   * than cart/update.js then /checkout, especially on iOS WebKit (Chrome/Safari).
   * Checkout applies whichever comma-separated codes are valid, while
   * /discount/A,B?redirect=/checkout drops all of them when one doesn't apply
   * (e.g. a carried-over pouches 5-OFF), and it's one redirect fewer.
   * @param {string[]} codes
   * @returns {string}
   */
  function getCheckoutUrlWithDiscountCodes(codes) {
    const checkout = getStoreCheckoutUrl();
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return checkout;
    const sep = checkout.includes('?') ? '&' : '?';
    return `${checkout}${sep}discount=${encodeURIComponent(list.join(','))}`;
  }

  /**
   * New gum code first so Shopify applies 50-OFF when it won't stack after 5-OFF.
   * @param {string | null | undefined} newCode
   * @param {string[] | null | undefined} preserveCodes
   * @returns {string[]}
   */
  function checkoutCodesWithNewFirst(newCode, preserveCodes) {
    const code = String(newCode ?? '').trim();
    return unionDiscountCodes(code ? [code] : [], preserveCodes);
  }

  function setPendingCheckoutDiscountUrl(newCode, preserveCodes) {
    const codes = checkoutCodesWithNewFirst(newCode, preserveCodes);
    window.__larineCheckoutDiscountUrl = codes.length
      ? getCheckoutUrlWithDiscountCodes(codes)
      : '';
  }

  /**
   * Bundle radio whose discount attrs should drive flavors pricing / checkout.
   * Prefers the tier that matches current flavor qty (same as selling-plan sync).
   * @param {HTMLElement | null | undefined} [modal]
   * @returns {HTMLInputElement | null}
   */
  function getBundleRadioForDiscount(modal) {
    const host = modal instanceof HTMLElement ? modal : getFlavorsModal();
    if (isJavvyPdp()) {
      let qty = host ? getFlavorsSelectionState(host).totalQty : 0;
      if (qty <= 0) qty = getSelectedBundleItemCount();
      return getJavvyDiscountRadioForQty(qty) || getActiveBundleRadio();
    }
    if (host) {
      const totalQty = getFlavorsSelectionState(host).totalQty;
      if (totalQty > 0) return getBundleRadioForFlavorTotalQty(totalQty);
    }
    return getActiveBundleRadio();
  }

  /**
   * Theme customizer and/or podcast LP subscription discount % from Liquid (display-only).
   * Javvy radios may override this per tier via data-subscription-discount-percent
   * (from variant compare-at vs price).
   * @param {HTMLElement} modal
   * @returns {number}
   */
  function getFlavorsSubscriptionDiscountPercent(modal) {
    if (isJavvyPdp()) {
      const radioPct = getBundleRadioForDiscount(modal)?.getAttribute(DATA_ATTR.subscriptionDiscountPercent);
      const fromRadio = Math.min(100, parsePositiveFloat(radioPct));
      if (fromRadio > 0) return fromRadio;
    }
    const dialog = modal?.querySelector('.nw-new-bundle-flavors-modal__dialog');
    const raw = dialog?.getAttribute('data-flavors-subscription-discount-percent');
    return Math.min(100, parsePositiveFloat(raw));
  }

  /**
   * @param {number} cents
   * @param {number} percent 0–100 (decimals supported)
   * @returns {number}
   */
  function applySubscriptionDiscountPercent(cents, percent) {
    const base = Math.round(Number(cents));
    const p = Math.min(100, Math.max(0, parseFloat(String(percent))));
    if (!Number.isFinite(base) || base <= 0 || !Number.isFinite(p) || p <= 0) return base;
    const raw = (base * (100 - p)) / 100;
    // Javvy only: $19.98/$19.99 at 50% must be $9.99, not $10.00.
    if (isJavvyPdp()) {
      const floored = Math.floor(raw);
      return Math.max(0, raw - floored > 0.5 ? floored + 1 : floored);
    }
    return Math.max(0, Math.round(raw));
  }

  /** @returns {boolean} Javvy: gum-tier-pricing function prices this page (flag from bundle-gums-javvy.liquid). */
  function javvyTierPricingFunctionEnabled() {
    return document.querySelector('[data-nw-tier-pricing-function="true"]') !== null;
  }

  /**
   * Discount code from the flavors modal (subscription only).
   * Javvy: `custom.discount_code` on the pack whose box count matches **current
   * flavor qty** (2/4/6/8+), not the PDP radio that opened the popup.
   * Gum / maxxing always get the modal `50-OFF`.
   * @returns {string}
   */
  function getFlavorsDiscountCode() {
    if (isJavvyPdp()) {
      if (javvyTierPricingFunctionEnabled()) return '';
      const modal = getFlavorsModal();
      let qty = modal ? getFlavorsSelectionState(modal).totalQty : 0;
      if (qty <= 0) qty = getSelectedBundleItemCount();
      const radio = getJavvyDiscountRadioForQty(qty);
      return String(radio?.getAttribute(DATA_ATTR.discountCode) ?? '').trim();
    }
    const modal = getFlavorsModal();
    const dialog = modal?.querySelector('.nw-new-bundle-flavors-modal__dialog');
    return String(dialog?.getAttribute('data-flavors-discount-code') ?? '').trim();
  }

  /**
   * Discount code only when subscription is the active purchase type.
   * @returns {string}
   */
  function getFlavorsDiscountCodeForActivePurchase() {
    if (activePurchaseType !== PURCHASE_SUBSCRIPTION) return '';
    return getFlavorsDiscountCode();
  }

  /**
   * @returns {boolean} Whether this block is configured (flavors_open_cart_drawer)
   * to open the cart drawer after checkout instead of redirecting.
   */
  function shouldOpenCartDrawerAfterFlavorsCheckout() {
    const modal = getFlavorsModal();
    const dialog = modal?.querySelector('.nw-new-bundle-flavors-modal__dialog');
    return dialog?.getAttribute('data-open-cart-drawer') === 'true';
  }

  /**
   * Predicts whether this modal's main CTA will end up redirecting straight
   * to checkout (vs. opening the cart drawer), so the button label can match
   * what's actually about to happen. Mirrors finishFlavorsCheckoutNavigation
   * + closeFlavorsModal(): the cart-drawer upsell flow always reopens the
   * drawer viewOnly (it's adding to an order already in progress, not this
   * page's own product, so it only redirects if the drawer is disabled
   * sitewide). A block configured with flavors_open_cart_drawer instead
   * *attempts* to open the drawer via cartDrawerCustom.open() — but that
   * still funnels through cart-drawer.js's openDrawer(), which redirects
   * when the cart drawer is disabled sitewide, this is a /pages/* URL with
   * checkout enforced, or this product's own "Force checkout redirect"
   * metafield is on.
   * @returns {boolean}
   */
  function flavorsCtaWillRedirectToCheckout() {
    if (window.__nwUpsellFlavorsFlow) return Boolean(window.__cartDrawerDisabled);
    if (!shouldOpenCartDrawerAfterFlavorsCheckout()) return true;
    const onPage = window.location.pathname.indexOf('/pages/') !== -1;
    return Boolean(
      window.__cartDrawerDisabled ||
      (window.__cartDrawerForceCheckoutOnPages && onPage) ||
      window.__cartDrawerForceCheckoutOnProduct
    );
  }

  /**
   * After flavors checkout: open the cart drawer when the block is configured
   * for it, otherwise redirect to Shopify checkout via /discount/CODE so the
   * first-order code is in the URL. Do not bounce through cartDrawerCustom.open()
   * when that would itself redirect to /checkout — that path drops the code.
   * @returns {Promise<void>}
   */
  async function finishFlavorsCheckoutNavigation() {
    // Cart-drawer upsell flow stays in the drawer unless the drawer is
    // disabled sitewide. PDP flow opens the drawer only when that is the
    // actual destination — not when force-checkout (product metafield, /pages/*,
    // or drawer disabled) would immediately navigate away.
    const isUpsellFlow = Boolean(window.__nwUpsellFlavorsFlow);
    const stayInDrawer = isUpsellFlow
      ? !window.__cartDrawerDisabled
      : !flavorsCtaWillRedirectToCheckout();

    if (stayInDrawer) {
      resetFlavorsMainCtaLoadingUi();
      closeCheckoutUpsellModal();
      closeFlavorsModal();
      if (window.cartDrawerCustom) {
        // window.__cartDrawerUpdating is still true here (this whole flow's
        // caller hasn't reached its finally block yet), which suppresses the
        // auto-detecting fetch interceptor for the add/clear/discount calls
        // already made — so without an explicit refresh, the drawer opens
        // showing whatever stale content it last had, relying entirely on
        // Shrine's own independent (and unpredictably timed) fetch wrapper
        // to eventually notice the mutation and dispatch a refresh. Calling
        // refresh() directly isn't gated by that flag, so it fires the sync
        // + section update immediately and deterministically instead.
        //
        // refresh() is also where free-gift tier lines get added
        // (cart-free-gifts.js's sync, run as the first step of that
        // pipeline) — open()/redirect must wait for it to finish rather than
        // firing immediately alongside it: on a force-checkout-on-product
        // page, open() navigates to checkout synchronously, and a same-tick
        // navigation cancels refresh()'s still-in-flight gift-add request,
        // silently dropping the tier gifts from the order.
        const openAfterRefresh = () => {
          if (typeof window.cartDrawerCustom.open === 'function') {
            window.cartDrawerCustom.open(isUpsellFlow);
          }
        };
        if (typeof window.cartDrawerCustom.refresh === 'function') {
          await Promise.resolve(window.cartDrawerCustom.refresh());
        }
        const code = getFlavorsDiscountCodeForActivePurchase();
        if (code || flavorsDiscountPreserve.length) {
          await applyCartDiscountCode(code, flavorsDiscountPreserve);
        }
        openAfterRefresh();
      }
      flavorsDiscountPreserve = [];
      return;
    }
    // Leave the CTA spinner on through the checkout navigation — /checkout's
    // redirect and first paint take another 1-2s, and a reset button there
    // reads as the click having stalled. pageshow resets it on back/bfcache.
    const cartAfterAdd = flavorsCartAfterAdd;
    flavorsCartAfterAdd = null;
    await redirectToStoreCheckout(
      getFlavorsDiscountCodeForActivePurchase() || undefined,
      cartAfterAdd
    );
    flavorsDiscountPreserve = [];
  }

  /**
   * @param {unknown} cart
   * @returns {string[]}
   */
  function discountCodesFromCart(cart) {
    const out = [];
    const seen = new Set();
    const push = (raw) => {
      const code = String(raw ?? '').trim();
      if (!code) return;
      const key = code.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(code);
    };
    if (cart && Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((entry) => {
        push(typeof entry === 'string' ? entry : entry && entry.code);
      });
    }
    if (cart && typeof cart.discount_code === 'string') push(cart.discount_code);
    return out;
  }

  /**
   * @param {string} nextCode
   * @returns {Promise<string[]>}
   */
  async function mergeCartDiscountCodes(nextCode) {
    const code = String(nextCode ?? '').trim();
    const cartUrl = cartAjaxJsUrl(window.routes?.cart_url, '/cart');
    let existing = [];
    try {
      const res = await _fetch(cartUrl, {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (res.ok) existing = discountCodesFromCart(await res.json());
    } catch {
      /* keep existing empty — still apply the new code */
    }
    if (!code) return existing;
    if (existing.some((c) => c.toUpperCase() === code.toUpperCase())) return existing;
    return existing.concat(code);
  }

  /**
   * Free gifts / tree lines are reconciled by cart-free-gifts.js, not by the
   * flavors add payload. Checkout navigation must wait for that sync — otherwise
   * the redirect races ahead and Shopify Checkout renders the pre-gift cart.
   * Coming back to the storefront then shows the gifts because sync finally
   * completed on this page.
   * @param {unknown} [cartData] current cart, so sync skips its own /cart.js read
   * @returns {Promise<void>}
   */
  async function syncFreeGiftsBeforeCheckout(cartData) {
    try {
      if (window.cartFreeGifts && typeof window.cartFreeGifts.sync === 'function') {
        await window.cartFreeGifts.sync(cartData || undefined);
        return;
      }
      if (window.cartDrawerCustom && typeof window.cartDrawerCustom.refresh === 'function') {
        await window.cartDrawerCustom.refresh();
      }
    } catch (e) {
      console.warn('[NwNewBundle] free-gift sync before checkout failed', e);
    }
  }

  /**
   * Go to checkout. When a code is present, navigate to /checkout?discount=CODE
   * (merged with codes already on the cart) instead of bare /checkout — cart/update.js
   * discounts often fail to follow the shopper onto Shopify Checkout on iOS.
   * @param {string | null | undefined} discountCode
   * @param {unknown} [cartData] cart as of right now, when the caller already has it
   */
  async function redirectToStoreCheckout(discountCode, cartData) {
    if (window.__larineIsGumIgHideChrome && window.__larineIsGumIgHideChrome()) {
      try {
        sessionStorage.setItem('larineBundleCheckoutRedirect', '1');
        localStorage.setItem('larineBundleCheckoutRedirect', '1');
      } catch {
        /* private mode */
      }
      try {
        var cookie = 'larineBundleCheckoutRedirect=1; path=/; max-age=3600; SameSite=Lax';
        if (location.protocol === 'https:') cookie += '; Secure';
        document.cookie = cookie;
      } catch {
        /* private mode */
      }
    }
    const code = String(discountCode ?? '').trim();
    setPendingCheckoutDiscountUrl(code, flavorsDiscountPreserve);

    // Only the gift sync has to land before navigating. The checkout URL below
    // applies every code on its own, so writing them with cart/update.js and
    // re-reading /cart.js first only added ~2s between click and checkout
    // (and raced the gift sync's own discount restore).
    await syncFreeGiftsBeforeCheckout(cartData);

    const finalCodes = checkoutCodesWithNewFirst(code, flavorsDiscountPreserve);
    const url = finalCodes.length
      ? getCheckoutUrlWithDiscountCodes(finalCodes)
      : getStoreCheckoutUrl();
    window.__larineCheckoutDiscountUrl = url;
    window.location.href = url;
  }

  // ---------------------------------------------------------------------------
  // Checkout upsell modal (post–flavors-modal, pre-checkout)
  // ---------------------------------------------------------------------------

  /**
   * @returns {HTMLElement | null}
   */
  function getCheckoutUpsellRoot() {
    const el = document.querySelector(SELECTOR.checkoutUpsellRoot);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getCheckoutUpsellModal() {
    const el = document.querySelector(SELECTOR.checkoutUpsellModal);
    return el instanceof HTMLElement ? el : null;
  }

  /**
   * @returns {boolean}
   */
  function isCheckoutUpsellEnabled() {
    const root = getCheckoutUpsellRoot();
    if (!root) return false;
    const raw = root.getAttribute('data-nw-checkout-upsell-enabled');
    return String(raw ?? '').trim().toLowerCase() !== 'false';
  }

  /**
   * @returns {HTMLElement | null}
   */
  function getCheckoutUpsellPanel() {
    const root = getCheckoutUpsellRoot();
    if (!root) return null;
    const panel = root.querySelector('[data-upsell-panel]');
    return panel instanceof HTMLElement ? panel : null;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {boolean}
   */
  function isCheckoutUpsellPanelAvailable(panel) {
    const raw = String(panel.getAttribute('data-upsell-available') ?? '').trim().toLowerCase();
    return raw !== 'false' && raw !== '0';
  }

  /**
   * @returns {number}
   */
  function getCheckoutUpsellSkipThreshold() {
    const root = getCheckoutUpsellRoot();
    const raw =
      root?.getAttribute('data-upsell-skip-at-boxes') ??
      root?.getAttribute('data-upsell-c-skip-at-boxes');
    if (raw == null || String(raw).trim() === '') return 6;
    const n = parsePositiveInt(raw);
    if (n === 0) return 0;
    return n;
  }

  /**
   * @param {number} totalBoxes
   * @returns {boolean}
   */
  function shouldShowCheckoutUpsell(totalBoxes) {
    if (!isCheckoutUpsellEnabled()) return false;
    const panel = getCheckoutUpsellPanel();
    if (!panel || !isCheckoutUpsellPanelAvailable(panel)) return false;
    const threshold = getCheckoutUpsellSkipThreshold();
    if (threshold > 0 && totalBoxes >= threshold) return false;
    return true;
  }

  const UPSELL_PURCHASE_SUBSCRIPTION = 'subscription';
  const UPSELL_PURCHASE_ONETIME = 'onetime';

  /**
   * @param {number} cents
   * @returns {string}
   */
  function formatCheckoutUpsellMoneyCents(cents) {
    const root = getCheckoutUpsellRoot();
    const currency = String(root?.getAttribute('data-shop-currency') ?? 'USD').trim() || 'USD';
    const moneyFormat = String(root?.getAttribute('data-money-format') ?? '').trim();
    const n = Number(cents) / 100;

    const fromShop = formatCentsWithShopMoneyFormat(moneyFormat, cents);
    if (fromShop) return fromShop;

    try {
      let out = new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency,
        currencyDisplay: 'narrowSymbol',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }).format(n);
      if (String(currency).toUpperCase() === 'USD' && out.startsWith('US$')) {
        out = out.replace(/^US\$/i, '$');
      }
      return out;
    } catch {
      return `${n.toFixed(2)} ${currency}`;
    }
  }

  /**
   * @param {HTMLElement} panel
   * @returns {'subscription' | 'onetime'}
   */
  function getCheckoutUpsellPurchaseType(panel) {
    const active = panel.querySelector('[data-nw-checkout-upsell-purchase-toggle][aria-pressed="true"]');
    const raw = String(active?.getAttribute('data-purchase') ?? UPSELL_PURCHASE_SUBSCRIPTION).trim().toLowerCase();
    return raw === UPSELL_PURCHASE_ONETIME ? UPSELL_PURCHASE_ONETIME : UPSELL_PURCHASE_SUBSCRIPTION;
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLElement | null}
   */
  function getSelectedCheckoutUpsellVariantCard(panel) {
    const card = panel.querySelector('[data-nw-checkout-upsell-variant][aria-pressed="true"]');
    return card instanceof HTMLElement ? card : null;
  }

  /**
   * Subscription savings % for the selected variant (unchanged when One-Time is selected).
   * @param {HTMLElement | null} card
   * @returns {number}
   */
  function getCheckoutUpsellDiscountPercent(card) {
    if (!(card instanceof HTMLElement)) return 0;

    const fromAttr = parseInt(String(card.getAttribute('data-discount-subscription-percent') ?? ''), 10);
    if (Number.isFinite(fromAttr) && fromAttr > 0) return fromAttr;

    const fullCents = parsePositiveInt(card.getAttribute('data-onetime-full-cents'));
    const subCents = parsePositiveInt(card.getAttribute('data-sub-cents'));
    if (!fullCents || !subCents || subCents >= fullCents) return 0;

    return Math.round(((fullCents - subCents) / fullCents) * 100);
  }

  /**
   * @param {HTMLElement} panel
   */
  function syncCheckoutUpsellDynamicDiscount(panel) {
    if (!(panel instanceof HTMLElement)) return;
    const card = getSelectedCheckoutUpsellVariantCard(panel);
    const percent = getCheckoutUpsellDiscountPercent(card);
    const text = percent > 0 ? String(percent) : '';

    panel.querySelectorAll('[data-nw-checkout-upsell-dynamic-discount]').forEach((el) => {
      el.textContent = text;
    });
  }

  /**
   * @param {HTMLElement} panel
   */
  function syncCheckoutUpsellPurchaseUi(panel) {
    if (!(panel instanceof HTMLElement)) return;
    const purchaseRoot = panel.querySelector('[data-nw-checkout-upsell-purchase]');
    const isOnetime = getCheckoutUpsellPurchaseType(panel) === UPSELL_PURCHASE_ONETIME;
    purchaseRoot?.classList.toggle('is-onetime', isOnetime);

    const card = getSelectedCheckoutUpsellVariantCard(panel);
    const rawCents = card
      ? isOnetime
        ? card.getAttribute('data-onetime-cents')
        : card.getAttribute('data-sub-cents')
      : null;
    const cents = parsePositiveInt(rawCents);
    const priceText = formatCheckoutUpsellMoneyCents(cents);

    panel.querySelectorAll('[data-nw-checkout-upsell-accept-price]').forEach((priceEl) => {
      if (priceEl instanceof HTMLElement) {
        priceEl.textContent = priceText;
      }
    });

    syncCheckoutUpsellDynamicDiscount(panel);
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLButtonElement} toggleBtn
   */
  function setCheckoutUpsellPurchaseType(panel, toggleBtn) {
    panel.querySelectorAll('[data-nw-checkout-upsell-purchase-toggle]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const active = btn === toggleBtn;
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    syncCheckoutUpsellPurchaseUi(panel);
  }

  /**
   * @param {HTMLElement} panel
   * @param {HTMLButtonElement} variantBtn
   */
  function setCheckoutUpsellVariant(panel, variantBtn) {
    panel.querySelectorAll('[data-nw-checkout-upsell-variant]').forEach((btn) => {
      if (!(btn instanceof HTMLButtonElement)) return;
      const active = btn === variantBtn;
      btn.classList.toggle('is-selected', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    });
    syncCheckoutUpsellPurchaseUi(panel);
  }

  /**
   * @returns {boolean}
   */
  function isCheckoutUpsellModalOpen() {
    const modal = getCheckoutUpsellModal();
    return Boolean(modal?.classList.contains(CLASS_NAME.checkoutUpsellModalOpen));
  }

  /** Cached native scrollbar width (px) for modal scroll-lock padding. */
  let cachedModalScrollbarGapPx = null;

  /**
   * Native scrollbar width. While body scroll is locked the scrollbar is hidden, so width reads as 0 —
   * reuse cached / CSS value, or briefly unlock to measure.
   * @returns {number}
   */
  function measureModalScrollbarGapPx() {
    if (cachedModalScrollbarGapPx != null && cachedModalScrollbarGapPx > 0) {
      return cachedModalScrollbarGapPx;
    }

    const fromStyle = document.documentElement.style.getPropertyValue('--nw-flavors-scrollbar-gap').trim();
    const fromStylePx = parseFloat(fromStyle);
    if (Number.isFinite(fromStylePx) && fromStylePx > 0) {
      cachedModalScrollbarGapPx = fromStylePx;
      return fromStylePx;
    }

    const fromComputed = getComputedStyle(document.documentElement)
      .getPropertyValue('--nw-flavors-scrollbar-gap')
      .trim();
    const fromComputedPx = parseFloat(fromComputed);
    if (Number.isFinite(fromComputedPx) && fromComputedPx > 0) {
      cachedModalScrollbarGapPx = fromComputedPx;
      return fromComputedPx;
    }

    const hadFlavorsLock = document.body.classList.contains(CLASS_NAME.bodyModalLock);
    const hadUpsellLock = document.body.classList.contains(CLASS_NAME.bodyCheckoutUpsellLock);
    if (hadFlavorsLock || hadUpsellLock) {
      document.body.classList.remove(CLASS_NAME.bodyModalLock);
      document.body.classList.remove(CLASS_NAME.bodyCheckoutUpsellLock);
    }

    const gap = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

    if (hadFlavorsLock) document.body.classList.add(CLASS_NAME.bodyModalLock);
    if (hadUpsellLock) document.body.classList.add(CLASS_NAME.bodyCheckoutUpsellLock);

    if (gap > 0) cachedModalScrollbarGapPx = gap;
    return gap;
  }

  /**
   * @param {number} gap
   */
  function setModalScrollbarGapPx(gap) {
    const px = Math.max(0, gap);
    if (px > 0) cachedModalScrollbarGapPx = px;
    document.documentElement.style.setProperty('--nw-flavors-scrollbar-gap', `${px}px`);
  }

  function clearModalScrollbarGapIfIdle() {
    if (isFlavorsModalOpen() || isCheckoutUpsellModalOpen()) return;
    cachedModalScrollbarGapPx = null;
    document.documentElement.style.removeProperty('--nw-flavors-scrollbar-gap');
  }

  function applyCheckoutUpsellScrollLock() {
    if (flavorsModalScrollUnlockTimer != null) {
      clearTimeout(flavorsModalScrollUnlockTimer);
      flavorsModalScrollUnlockTimer = null;
    }
    setModalScrollbarGapPx(measureModalScrollbarGapPx());
    document.body.classList.add(CLASS_NAME.bodyCheckoutUpsellLock);
    if (isFlavorsModalOpen()) {
      document.body.classList.add(CLASS_NAME.bodyModalLock);
    }
  }

  function removeCheckoutUpsellScrollLock() {
    document.body.classList.remove(CLASS_NAME.bodyCheckoutUpsellLock);
    clearModalScrollbarGapIfIdle();
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLButtonElement | null}
   */
  function getVisibleCheckoutUpsellAcceptButton(panel) {
    return (
      getCheckoutUpsellAcceptButtons(panel).find((btn) => {
        return btn.offsetParent !== null || window.getComputedStyle(btn).display !== 'none';
      }) ?? null
    );
  }

  function openCheckoutUpsellModal() {
    const modal = getCheckoutUpsellModal();
    const panel = getCheckoutUpsellPanel();
    if (!modal || !panel) {
      finishFlavorsCheckoutNavigation();
      return;
    }

    panel.classList.remove('hidden');
    panel.hidden = false;

    modal.classList.add(CLASS_NAME.checkoutUpsellModalOpen);
    modal.setAttribute('aria-hidden', 'false');
    applyCheckoutUpsellScrollLock();
    syncCheckoutUpsellPurchaseUi(panel);

    const acceptBtn = getVisibleCheckoutUpsellAcceptButton(panel);
    if (acceptBtn) {
      window.requestAnimationFrame(() => acceptBtn.focus());
    }
  }

  function closeCheckoutUpsellModal() {
    const modal = getCheckoutUpsellModal();
    if (!modal) return;
    modal.classList.remove(CLASS_NAME.checkoutUpsellModalOpen);
    modal.setAttribute('aria-hidden', 'true');
    modal.querySelectorAll('[data-upsell-panel]').forEach((el) => {
      if (!(el instanceof HTMLElement)) return;
      el.classList.add('hidden');
      el.hidden = true;
    });
    removeCheckoutUpsellScrollLock();
  }

  /**
   * @param {HTMLElement} panel
   * @returns {HTMLButtonElement[]}
   */
  function getCheckoutUpsellAcceptButtons(panel) {
    if (!(panel instanceof HTMLElement)) return [];
    return Array.from(panel.querySelectorAll(SELECTOR.checkoutUpsellAccept)).filter(
      (btn) => btn instanceof HTMLButtonElement
    );
  }

  /**
   * @param {HTMLElement} panel
   * @param {boolean} isLoading
   */
  function setCheckoutUpsellAcceptLoadingUi(panel, isLoading) {
    getCheckoutUpsellAcceptButtons(panel).forEach((btn) => {
      const spinner = btn.querySelector('.loading-overlay__spinner');
      btn.classList.toggle('loading', isLoading);
      btn.disabled = isLoading;
      if (isLoading) btn.setAttribute('aria-busy', 'true');
      else btn.removeAttribute('aria-busy');
      if (spinner instanceof HTMLElement) spinner.classList.toggle('hidden', !isLoading);
    });
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  function resetCheckoutUpsellAcceptUi(btn) {
    const panel = btn.closest('[data-upsell-panel]');
    if (panel instanceof HTMLElement) {
      setCheckoutUpsellAcceptLoadingUi(panel, false);
    }
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  async function handleCheckoutUpsellAccept(btn) {
    const panel = btn.closest('[data-upsell-panel]');
    if (!(panel instanceof HTMLElement)) return;

    const card = getSelectedCheckoutUpsellVariantCard(panel);
    const variantIdStr = String(card?.getAttribute('data-variant-id') ?? '').trim();
    const variantId = Number(variantIdStr);
    if (!Number.isFinite(variantId) || variantId <= 0) {
      finishFlavorsCheckoutNavigation();
      return;
    }

    const purchaseType = getCheckoutUpsellPurchaseType(panel);
    const sellingPlanRaw = String(card?.getAttribute('data-selling-plan-id') ?? '').trim();
    const sellingPlanId = parsePositiveInt(sellingPlanRaw);
    const variantQtyRaw = String(card?.getAttribute('data-variant-qty') ?? '1').trim();
    const variantQty = Math.max(1, parsePositiveInt(variantQtyRaw) || 1);
    const discountCodeRaw = String(panel.getAttribute('data-upsell-discount-code') ?? '').trim();
    const discountCode = purchaseType === UPSELL_PURCHASE_ONETIME ? discountCodeRaw : '';
    const upsellSource = String(panel.getAttribute('data-upsell-source') ?? '').trim();

    setCheckoutUpsellAcceptLoadingUi(panel, true);

    try {
      const addUrl = cartAjaxJsUrl(window.routes?.cart_add_url, '/cart/add');
      /** @type {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }} */
      const lineItem = { id: variantId, quantity: variantQty };
      if (purchaseType === UPSELL_PURCHASE_SUBSCRIPTION && sellingPlanId > 0) {
        lineItem.selling_plan = sellingPlanId;
      }
      if (upsellSource) {
        lineItem.properties = { '_Upsell source': upsellSource };
      }
      const addRes = await _fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [lineItem] }),
      });

      if (!addRes.ok) {
        let msg = 'Could not add upsell to cart';
        try {
          const err = await addRes.json();
          if (err && typeof err.description === 'string') msg = err.description;
          else if (err && typeof err.message === 'string') msg = err.message;
        } catch {
          /* keep default */
        }
        throw new Error(msg);
      }

      await redirectToStoreCheckout(
        discountCode || getFlavorsDiscountCodeForActivePurchase() || undefined
      );
    } catch (e) {
      console.error('[NwNewBundle] Checkout upsell accept failed', e);
      resetCheckoutUpsellAcceptUi(btn);
      finishFlavorsCheckoutNavigation();
    }
  }

  /**
   * @param {MouseEvent} event
   */
  function onCheckoutUpsellPointerDown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const modal = getCheckoutUpsellModal();
    if (!modal || !isCheckoutUpsellModalOpen() || !modal.contains(target)) return;

    const panel = getCheckoutUpsellPanel();
    if (!panel) return;

    const purchaseToggle = target.closest('[data-nw-checkout-upsell-purchase-toggle]');
    if (purchaseToggle instanceof HTMLButtonElement) {
      event.preventDefault();
      setCheckoutUpsellPurchaseType(panel, purchaseToggle);
      return;
    }

    const variantBtn = target.closest('[data-nw-checkout-upsell-variant]');
    if (variantBtn instanceof HTMLButtonElement && !variantBtn.disabled) {
      event.preventDefault();
      setCheckoutUpsellVariant(panel, variantBtn);
      return;
    }

    const acceptBtn = target.closest(SELECTOR.checkoutUpsellAccept);
    if (acceptBtn instanceof HTMLButtonElement) {
      event.preventDefault();
      void handleCheckoutUpsellAccept(acceptBtn);
      return;
    }

    if (target.closest(SELECTOR.checkoutUpsellDecline) || target.closest(SELECTOR.checkoutUpsellClose)) {
      event.preventDefault();
      finishFlavorsCheckoutNavigation();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  function onCheckoutUpsellKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!isCheckoutUpsellModalOpen()) return;
    event.preventDefault();
    finishFlavorsCheckoutNavigation();
  }

  function bindCheckoutUpsellEvents() {
    document.addEventListener('click', onCheckoutUpsellPointerDown);
    document.addEventListener('keydown', onCheckoutUpsellKeydown);
  }

  /**
   * After bundle cart add: show upsell overlay (flavors modal stays open underneath) or redirect.
   * @param {number} totalBoxes
   * @returns {Promise<void>}
   */
  async function finishFlavorCheckoutFlow(totalBoxes) {
    if (shouldShowCheckoutUpsell(totalBoxes)) {
      resetFlavorsMainCtaLoadingUi();
      openCheckoutUpsellModal();
      return;
    }

    await finishFlavorsCheckoutNavigation();
  }

  /**
   * @typedef {{ id: number, quantity: number, selling_plan?: number, properties?: Record<string, string> }} NwCartAddItem
   */

  /**
   * Compact javvy qty→plan / gift-tier map stored on flavor lines (and localStorage)
   * so the cart can remap selling plans + gifts after checkout-return qty edits.
   * @returns {Record<string, string> | null}
   */
  /**
   * Marks flavor lines added from the v3 PDP so the cart can tell them apart.
   * @returns {Record<string, string> | null}
   */
  function getV3CartLineProperties() {
    if (!document.querySelector('.nw-new-bundle-options--v3')) return null;
    return { _from_pdp: 'v3' };
  }

  function getJavvyCartLineProperties() {
    // A/B gift-rules pages ride the same line config (gift tiers only, no plans)
    // so the extra gift follows qty edits after leaving the page.
    if (!document.querySelector('.product--javvy, [data-nw-gift-ab]')) return null;

    /** @type {number[][]} */
    const plans = [];
    document.querySelectorAll('.product--javvy input[name="nw-new-bundle-option"]').forEach((el) => {
      if (!(el instanceof HTMLInputElement)) return;
      const minQty = parsePositiveInt(el.getAttribute('data-item-count'));
      const sellingPlanId = parsePositiveInt(el.getAttribute('data-flavors-selling-plan-id'));
      if (minQty > 0 && sellingPlanId > 0) plans.push([minQty, sellingPlanId]);
    });

    /** @type {number[][]} */
    let gifts = [];
    const node = document.querySelector('[data-nw-maxxing-gift-tiers]');
    if (node) {
      try {
        const parsed = JSON.parse(node.textContent || '[]');
        if (Array.isArray(parsed)) {
          gifts = parsed
            .filter((t) => t && Number(t.variantId) > 0)
            .map((t) => [Number(t.variantId), Number(t.minQty) || 0, Number(t.quantity) || 1]);
        }
      } catch {
        gifts = [];
      }
    }

    if (!plans.length && !gifts.length) return null;

    const cfg = { p: plans, g: gifts };
    try {
      localStorage.setItem('larineJavvyCartTiers', JSON.stringify(cfg));
    } catch {
      /* ignore quota / private mode */
    }
    return { _javvy_cfg: JSON.stringify(cfg) };
  }

  /**
   * Per-PDP "Clear cart before adding bundle" from `.nw-new-bundle-flavors-pdp-config`
   * (NW New Bundle Flavors block on the Gum PDP). Returns null when absent.
   * @param {Element | null | undefined} opener
   * @returns {boolean | null}
   */
  function resolvePdpClearCartBeforeCheckoutFromOpener(opener) {
    if (!(opener instanceof Element)) return null;
    const scope = opener.closest('product-info, .product__info-container, [data-section]');
    if (!scope) return null;
    const config = scope.querySelector('.nw-new-bundle-flavors-pdp-config');
    if (!config) return null;
    const raw = config.getAttribute('data-nw-clear-cart-before-checkout');
    if (raw == null) return null;
    return String(raw).trim().toLowerCase() !== 'false';
  }

  /**
   * @param {HTMLElement} modal
   * @returns {boolean}
   */
  function shouldClearCartBeforeFlavorCheckout(modal) {
    // Cart-drawer upsell flow adds to an order already in progress — never
    // clear it, regardless of this product's own PDP setting (that setting
    // governs the primary PDP purchase flow, an unrelated business rule).
    if (window.__nwUpsellFlavorsFlow) return false;
    if (flavorsPdpClearCartOverride !== null) return flavorsPdpClearCartOverride;
    const dialog = modal.querySelector('.nw-new-bundle-flavors-modal__dialog');
    const raw = dialog?.getAttribute('data-nw-clear-cart-before-checkout');
    if (raw == null) return true;
    return String(raw).trim().toLowerCase() !== 'false';
  }

  /** Prevents overlapping flavors checkout requests (double-click / race). */
  let flavorsCheckoutInFlight = false;
  /** Codes already on the cart before this flavors add — re-applied after gift sync. */
  let flavorsDiscountPreserve = [];
  /**
   * Cart read when the flavors modal opens, so the CTA doesn't wait on a
   * /cart.js round trip before cart/add.js. The cart only changes through the
   * drawer or discount links, neither reachable while the modal is open.
   * @type {Promise<unknown> | null}
   */
  let flavorsCartPrefetch = null;
  /**
   * Cart rebuilt from that read plus the cart/add.js response, handed to the
   * gift sync so it skips another /cart.js — right after an add, that read
   * queues behind the cart calls every app on the page fires and takes ~1s.
   * @type {{ items: unknown[] } | null}
   */
  let flavorsCartAfterAdd = null;

  /**
   * Gift/tree lines cart-free-gifts.js would add once these flavor lines are in
   * the cart, so they can go in the same cart/add.js. Anything it gets wrong is
   * still fixed by the sync that runs before checkout.
   * @param {HTMLElement} modal
   * @param {unknown} cartBefore
   * @param {NwCartAddItem[]} items
   * @returns {NwCartAddItem[]}
   */
  function plannedFreeGiftItems(modal, cartBefore, items) {
    const planner = window.cartFreeGifts && window.cartFreeGifts.plan;
    if (typeof planner !== 'function') return [];
    if (!cartBefore || !Array.isArray(cartBefore.items)) return [];
    const fallbackProductId = Number(window.__cartFreeGiftsConfig?.productId) || 0;
    const pending = items.map((item, i) => {
      const input = modal.querySelector(`[data-flavor-input][data-flavor-id="${item.id}"]`);
      const productId = parsePositiveInt(input?.getAttribute('data-flavor-product-id')) || fallbackProductId;
      return {
        key: `nw-pending-${i}`,
        variant_id: item.id,
        product_id: productId,
        quantity: item.quantity,
        // planner only checks > 0 to count a line as a paid box
        final_price: 1,
        properties: item.properties || {},
        selling_plan_allocation: item.selling_plan ? { selling_plan: { id: item.selling_plan } } : null,
      };
    });
    try {
      const adds = planner({ ...cartBefore, items: cartBefore.items.concat(pending) });
      return Array.isArray(adds) ? adds : [];
    } catch (e) {
      console.warn('[NwNewBundle] free-gift plan failed', e);
      return [];
    }
  }

  /** @returns {Promise<unknown>} cart JSON, or null when the read fails */
  function fetchCartJson() {
    const cartUrl = cartAjaxJsUrl(window.routes?.cart_url, '/cart');
    return _fetch(cartUrl, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then((res) => (res.ok ? res.json() : null))
      .catch(() => null);
  }

  /**
   * cart/add.js returns each touched line as it now sits in the cart (merged
   * quantity, same key), so replacing or appending those lines on the cart from
   * before the add gives the cart after it.
   * @param {unknown} before
   * @param {unknown} added cart/add.js response
   * @returns {{ items: unknown[] } | null}
   */
  function cartAfterAddFrom(before, added) {
    const beforeItems = before && Array.isArray(before.items) ? before.items : null;
    const addedItems = added && Array.isArray(added.items) ? added.items : null;
    if (!beforeItems || !addedItems) return null;
    const items = beforeItems.slice();
    const planOf = (item) => item?.selling_plan_allocation?.selling_plan?.id ?? null;
    const sameLine = (item, line) =>
      item.variant_id === line.variant_id &&
      planOf(item) === planOf(line) &&
      JSON.stringify(item.properties || {}) === JSON.stringify(line.properties || {});
    addedItems.forEach((line) => {
      // A merged line can come back under a new key (its discount allocation changed
      // with the quantity), so fall back to variant + plan + properties; otherwise the
      // old line is counted next to the merged one and gift tiers see too many boxes.
      let idx = items.findIndex((item) => item && item.key === line.key);
      if (idx < 0) idx = items.findIndex((item) => item && sameLine(item, line));
      if (idx >= 0) items[idx] = line;
      else items.push(line);
    });
    return { ...before, items };
  }

  function mergeFlavorCartLine(map, variantIdStr, qty, sellingPlanId, properties) {
    const id = Number(variantIdStr);
    if (!Number.isFinite(id) || id <= 0 || qty <= 0) return;
    const sp =
      sellingPlanId != null && Number.isFinite(Number(sellingPlanId)) && Number(sellingPlanId) > 0
        ? Number(sellingPlanId)
        : undefined;
    const key = `${id}:${sp ?? ''}`;
    const prev = map.get(key);
    if (prev) {
      prev.quantity += qty;
      if (!prev.properties && properties) prev.properties = properties;
      return;
    }
    /** @type {NwCartAddItem} */
    const lineItem = { id, quantity: qty };
    if (sp != null) lineItem.selling_plan = sp;
    if (properties) lineItem.properties = properties;
    map.set(key, lineItem);
  }


  /**
   * @param {string} addUrl
   * @param {NwCartAddItem[]} items
   * @returns {Promise<Response>}
   */
  function postCartAddItems(addUrl, items) {
    return _fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    });
  }

  /**
   * @param {unknown} cart
   * @param {string} code
   * @returns {boolean}
   */
  function cartHasApplicableCode(cart, code) {
    const upper = String(code ?? '').trim().toUpperCase();
    if (!upper) return true;
    const entries = Array.isArray(cart && cart.discount_codes) ? cart.discount_codes : [];
    return entries.some((entry) => {
      const name = String(
        typeof entry === 'string' ? entry : entry && entry.code ? entry.code : ''
      ).toUpperCase();
      if (name !== upper) return false;
      if (entry && typeof entry === 'object' && 'applicable' in entry) {
        return Boolean(entry.applicable);
      }
      return true;
    });
  }

  /**
   * Hits /discount/CODE without following the redirect — same trick gum-upsell
   * uses so Shopify stamps the code onto the cart cookie.
   * @param {string[]} codes
   * @returns {Promise<boolean>}
   */
  async function pingDiscountEndpoint(codes) {
    const list = (Array.isArray(codes) ? codes : [])
      .map((c) => String(c ?? '').trim())
      .filter(Boolean);
    if (!list.length) return false;
    const url = `${shopifyRootPath()}discount/${list.map((c) => encodeURIComponent(c)).join(',')}`;
    try {
      await _fetch(url, {
        method: 'GET',
        credentials: 'same-origin',
        redirect: 'manual',
      });
      return true;
    } catch (e) {
      console.warn('[NwNewBundle] GET /discount fallback failed', e);
      return false;
    }
  }

  /**
   * @param {string} discount
   * @returns {Promise<{ ok: boolean, cart: unknown }>}
   */
  async function postCartDiscount(discount, attributes) {
    const updateUrl = cartAjaxJsUrl(window.routes?.cart_update_url, '/cart/update');
    const res = await _fetch(updateUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(attributes ? { discount, attributes } : { discount }),
    });
    let cart = null;
    if (res.ok) {
      try {
        cart = await res.json();
      } catch {
        cart = null;
      }
    }
    return { ok: res.ok, cart };
  }

  /**
   * @param {string[]} base
   * @param {string[] | null | undefined} extra
   * @returns {string[]}
   */
  function unionDiscountCodes(base, extra) {
    const out = Array.isArray(base) ? base.slice() : [];
    const seen = new Set(out.map((c) => String(c).toUpperCase()));
    (Array.isArray(extra) ? extra : []).forEach((raw) => {
      const code = String(raw ?? '').trim();
      if (!code) return;
      const key = code.toUpperCase();
      if (seen.has(key)) return;
      seen.add(key);
      out.push(code);
    });
    return out;
  }

  /**
   * Applies a Shopify discount code to the cart after lines are added.
   * Merges with codes already on the cart — a single `discount` value replaces the list.
   * `preserveCodes` keeps codes that cart/add.js may have stripped (e.g. pouches 5-OFF).
   * @param {string} discountCode
   * @param {string[] | null | undefined} [preserveCodes]
   * @returns {Promise<boolean>} true when the update succeeds
   */
  async function applyCartDiscountCode(discountCode, preserveCodes) {
    const code = String(discountCode ?? '').trim();
    let merged = await mergeCartDiscountCodes(code);
    merged = unionDiscountCodes(merged, preserveCodes);
    if (!merged.length) return true;

    try {
      const { ok, cart } = await postCartDiscount(merged.join(','));
      if (ok && (!code || cartHasApplicableCode(cart, code))) return true;
      console.warn('[NwNewBundle] cart/update discount failed or not applicable', {
        code,
        merged,
        ok,
        discount_codes: cart && cart.discount_codes,
      });
    } catch (e) {
      console.warn('[NwNewBundle] cart/update discount error', e);
    }
    return pingDiscountEndpoint(merged);
  }

  /**
   * @param {Response} res
   * @returns {Promise<string>}
   */
  async function readCartAddErrorMessage(res) {
    let msg = 'Could not add items to cart';
    try {
      const err = await res.json();
      if (err && typeof err.description === 'string') msg = err.description;
      else if (err && typeof err.message === 'string') msg = err.message;
      else if (err && typeof err.status === 'string') msg = err.status;
    } catch {
      /* keep default */
    }
    return msg;
  }

  /**
   * @param {string} clearUrl
   * @returns {Promise<void>}
   */
  /**
   * @param {string} clearUrl
   * @param {boolean} [clearAovTest] also drop the aov_test attribute in the same cart/update.js
   */
  async function clearCartForFlavorCheckout(clearUrl, clearAovTest) {
    const clearRes = await _fetch(clearUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!clearRes.ok) throw new Error('Could not clear cart');
    try {
      await postCartDiscount('', clearAovTest ? { aov_test: '' } : undefined);
    } catch (e) {
      console.warn('[NwNewBundle] could not clear leftover discount codes', e);
    }
  }

  /**
   * Clears cart, adds bundle lines, redirects to checkout.
   * @param {HTMLButtonElement} btn [data-main-cta]
   */
  async function handleFlavorsMainCta(btn) {
    if (flavorsCheckoutInFlight) return;

    const modal = getFlavorsModal();
    if (!modal || !(btn instanceof HTMLButtonElement)) return;

    const selection = getFlavorsSelectionState(modal);
    if (!flavorsSelectionMeetsRequirements(modal, selection)) return;

    syncFlavorInputsWithBundleSellingPlan(modal);
    const selectionAfterSync = getFlavorsSelectionState(modal);

    const spinner = btn.querySelector('.loading-overlay__spinner');

    flavorsCheckoutInFlight = true;
    btn.classList.add('loading');
    btn.disabled = true;
    btn.setAttribute('aria-disabled', 'true');
    btn.setAttribute('aria-busy', 'true');
    if (spinner instanceof HTMLElement) spinner.classList.remove('hidden');

    // Without this, cart-drawer.js's global fetch interceptor also sees each
    // of this flow's own cart/clear, cart/add, and discount mutations and
    // fires its own runPipeline() for every one of them — on top of the
    // single explicit cart:open dispatch at the end that's actually meant to
    // refresh the drawer. Silences those, leaving just the one real refresh.
    window.__cartDrawerUpdating = true;

    try {
      /** @type {Map<string, NwCartAddItem>} */
      const flavorMap = new Map();
      const isSub = activePurchaseType === PURCHASE_SUBSCRIPTION;
      const javvyProps = getJavvyCartLineProperties() || getV3CartLineProperties();

      for (let i = 0; i < selectionAfterSync.lines.length; i += 1) {
        const line = selectionAfterSync.lines[i];
        if (line.qty <= 0) continue;
        const planRaw = line.input.getAttribute('data-flavor-selling-plan-id');
        const planId = parsePositiveInt(planRaw);
        if (isSub && planId > 0) {
          mergeFlavorCartLine(flavorMap, line.flavorId, line.qty, planId, javvyProps);
        } else {
          mergeFlavorCartLine(flavorMap, line.flavorId, line.qty, undefined, javvyProps);
        }
      }

      // Tier gifts and the tree-planted line are no longer added here —
      // cart-free-gifts.js reconciles them continuously (add/remove/qty
      // change) from theme settings, scoped to this product's quantity.
      // Adding them here too would fight that system over the same variants.
      const items = [...flavorMap.values()];
      if (!items.length) throw new Error('No items to add');

      const addUrl = cartAjaxJsUrl(window.routes?.cart_add_url, '/cart/add');
      const shouldClear = shouldClearCartBeforeFlavorCheckout(modal);
      const clearUrl = cartAjaxJsUrl(window.routes?.cart_clear_url, '/cart/clear');
      const cartPrefetch = flavorsCartPrefetch;
      flavorsCartPrefetch = null;
      flavorsCartAfterAdd = null;
      const cartBefore = shouldClear
        ? { items: [], discount_codes: [] }
        : await (cartPrefetch || fetchCartJson());
      const clearAovTest =
        !window.__nwUpsellFlavorsFlow && !isAbTaggedPdp() && (shouldClear || Boolean(cartBefore?.attributes?.aov_test));
      // When the cart gets cleared below, aov_test is dropped in that same
      // cart/update.js instead of costing its own round trip here.
      if (clearAovTest && !shouldClear) {
        await _fetch(cartAjaxJsUrl(window.routes?.cart_update_url, '/cart/update'), {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ attributes: { aov_test: '' } }),
        }).catch(() => null);
      }
      flavorsDiscountPreserve = discountCodesFromCart(cartBefore);
      if (isJavvyPdp()) {
        flavorsDiscountPreserve = stripJavvyTierDiscountCodes(flavorsDiscountPreserve);
      }
      const discountCode = getFlavorsDiscountCodeForActivePurchase();
      if (flavorsCtaWillRedirectToCheckout()) {
        setPendingCheckoutDiscountUrl(discountCode, flavorsDiscountPreserve);
      }

      if (shouldClear) {
        await clearCartForFlavorCheckout(clearUrl, clearAovTest);
      }

      // Tier gifts ride along in the same cart/add.js (planned by
      // cart-free-gifts.js itself), so its sync before checkout has nothing
      // left to add instead of firing a second add.
      const giftItems = plannedFreeGiftItems(modal, cartBefore, items);
      let addRes = await postCartAddItems(addUrl, items.concat(giftItems));

      // A failed cart/add.js adds nothing, so a gift line that can't be added
      // never blocks the boxes: retry without it and the sync adds what it can.
      if (!addRes.ok && giftItems.length) {
        addRes = await postCartAddItems(addUrl, items);
      }

      if (!addRes.ok && shouldClear) {
        await clearCartForFlavorCheckout(clearUrl, clearAovTest);
        addRes = await postCartAddItems(addUrl, items);
      }

      if (!addRes.ok) {
        const msg = await readCartAddErrorMessage(addRes);
        console.error('[NwNewBundle] Flavors cart/add failed', {
          status: addRes.status,
          message: msg,
          items,
          purchaseType: activePurchaseType,
        });
        throw new Error(msg);
      }

      flavorsCartAfterAdd = cartAfterAddFrom(
        cartBefore,
        await addRes.json().catch(() => null)
      );

      // Straight-to-checkout skips cart/update.js here — redirectToStoreCheckout
      // applies 50-OFF via /checkout?discount=50-OFF,5-OFF (new code first) so a
      // force-checkout race cannot land on bare /checkout.
      const goingStraightToCheckout =
        flavorsCtaWillRedirectToCheckout() &&
        !shouldShowCheckoutUpsell(selectionAfterSync.totalQty);
      if (discountCode && !goingStraightToCheckout) {
        await applyCartDiscountCode(discountCode, flavorsDiscountPreserve);
      }

      await finishFlavorCheckoutFlow(selectionAfterSync.totalQty);
    } catch (e) {
      console.error('[NwNewBundle] Flavors checkout failed', e);
      flavorsDiscountPreserve = [];
      window.__larineCheckoutDiscountUrl = '';
      resetFlavorsMainCtaLoadingUi();
    } finally {
      flavorsCheckoutInFlight = false;
      window.__cartDrawerUpdating = false;
    }
  }

  /**
   * Sets `--nw-tier-progress` (0–1) so the fill ends at the **center** of the last unlocked
   * milestone (equal-width grid), not past it toward the next locked tier.
   * @param {HTMLElement} modal
   * @param {number} total
   * @param {number} cents
   */
  function updateFlavorTierTrackProgress(modal, total, cents) {
    const tierRoot = modal.querySelector('.nw-new-bundle-flavors-tier');
    if (!tierRoot) return;

    const slots = parseFlavorTierSlots(modal).filter((s) => s.min > 0 || s.minCents > 0);
    if (!slots.length) {
      tierRoot.style.setProperty('--nw-tier-progress', '0');
      return;
    }

    const n = slots.length;
    const tierIsUnlocked = (slot) =>
      isV4FlavorsModal(modal)
        ? isV4FlavorTierUnlocked(slot, total, cents)
        : isFlavorTierUnlocked(slot, total, cents);
    let lastUnlockedIdx = -1;
    for (let i = 0; i < n; i += 1) {
      if (tierIsUnlocked(slots[i])) lastUnlockedIdx = i;
    }

    let progress = 0;
    if (lastUnlockedIdx < 0) {
      progress = 0;
    } else if (lastUnlockedIdx === n - 1) {
      progress = 1;
    } else {
      progress = (lastUnlockedIdx + 0.5) / n;
    }

    tierRoot.style.setProperty(
      '--nw-tier-progress',
      String(Math.min(1, Math.max(0, progress))),
    );
  }

  /**
   * Cart-drawer-context-only header (see .nw-cart-tier-header CSS / markup in
   * nw-new-bundle-flavors.liquid). Mirrors the tier data already rendered for
   * .nw-new-bundle-flavors-tier__step (data-tier-min-boxes etc. + label text)
   * into a differently-styled progress header, instead of duplicating this
   * file's tier-computation Liquid a second time.
   * @param {HTMLElement} modal
   * @param {number} total
   * @param {number} cents
   */
  function updateCartTierHeader(modal, total, cents) {
    const header = modal.querySelector('[data-nw-cart-tier-header]');
    if (!header) return;

    const sourceSteps = Array.from(modal.querySelectorAll('.nw-new-bundle-flavors-tier__step'));
    const headerSteps = Array.from(header.querySelectorAll('[data-nw-cart-tier-step]'));
    if (!sourceSteps.length || !headerSteps.length) return;

    // This header is cart-drawer-only (hidden entirely on the PDP via CSS) —
    // free shipping isn't offered as a reward here, it's a quick add-on to
    // an order already in progress, not a full checkout — so drop that tier
    // rather than just relabeling it. Any headerSteps beyond the remaining
    // slot count are hidden below (slots[i] undefined).
    const slots = sourceSteps
      .map((step) => {
        const min = parsePositiveInt(step.getAttribute('data-tier-min-boxes'));
        const minCents = parsePositiveInt(step.getAttribute('data-tier-min-cents'));
        const freeShipping = step.getAttribute('data-tier-free-shipping') === 'true';
        const labelEl = step.querySelector('.nw-new-bundle-flavors-tier__label');
        const label = (labelEl?.textContent || '').replace(/\s+/g, ' ').trim();
        return { min, minCents, freeShipping, label };
      })
      .filter((s) => !s.freeShipping);

    let lastUnlockedIdx = -1;
    headerSteps.forEach((stepEl, i) => {
      const slot = slots[i];
      if (!slot) {
        stepEl.hidden = true;
        return;
      }
      stepEl.hidden = false;
      const unlocked = isFlavorTierUnlocked(slot, total, cents);
      if (unlocked) lastUnlockedIdx = i;
      stepEl.classList.toggle('is-achieved', unlocked);
      stepEl.classList.toggle('is-shipping', slot.freeShipping);
      const labelEl = stepEl.querySelector('[data-nw-cart-tier-label]');
      if (labelEl) labelEl.textContent = slot.label;
    });

    const n = slots.length;
    let progress = 0;
    if (n > 0) {
      if (lastUnlockedIdx < 0) progress = 0;
      else if (lastUnlockedIdx === n - 1) progress = 1;
      else progress = (lastUnlockedIdx + 0.5) / n;
    }
    header.style.setProperty('--nw-cart-tier-progress', String(Math.min(1, Math.max(0, progress))));

    const headingEl = header.querySelector('[data-nw-cart-tier-heading]');
    if (!headingEl) return;

    const allUnlocked = n > 0 && lastUnlockedIdx === n - 1;
    if (!n) {
      headingEl.textContent = '';
      return;
    }
    if (allUnlocked) {
      headingEl.textContent = 'You have earned all rewards!';
      return;
    }

    const next = slots.find((s) => !isFlavorTierUnlocked(s, total, cents));
    if (!next) {
      headingEl.textContent = '';
      return;
    }
    if (next.freeShipping) {
      const needCents = Math.max(0, next.minCents - cents);
      const needMoney = formatFlavorMoneyCents(modal, needCents);
      headingEl.textContent = `Add ${needMoney} for ${next.label}!`;
      return;
    }
    headingEl.textContent = `Order ${next.min}+ Boxes and get ${next.label}`;
  }

  /**
   * Reference per-box price (cents) for the active purchase type, used to
   * put box-count tiers and the cents-based free-shipping tier on the same
   * scale (see reorderFlavorTierSteps). Uses the current selection's own
   * blended average when boxes are already primed — exactly consistent
   * with the free-shipping unlock check itself, which is also just
   * `cents >= minCents` — falling back to the first flavor row's own
   * per-unit price before anything's selected yet.
   * @param {HTMLElement} modal
   * @param {number} total
   * @param {number} cents
   * @returns {number}
   */
  function getFlavorTierReferencePerBoxCents(modal, total, cents) {
    if (total > 0) return cents / total;
    const firstInput = modal.querySelector('[data-flavor-input]');
    if (!(firstInput instanceof HTMLInputElement)) return 0;
    const isSub = activePurchaseType === PURCHASE_SUBSCRIPTION;
    const subCents = parseCentsAttr(firstInput, 'data-flavor-sub-unit-cents');
    const otCents = parseCentsAttr(firstInput, 'data-flavor-onetime-unit-cents');
    return isSub && subCents > 0 ? subCents : otCents;
  }

  /**
   * Reorders the tier progress track's steps (and keeps their
   * data-tier-slot attribute in sync, since parseFlavorTierSlots —
   * consumed by updateFlavorTierTrackProgress and
   * syncFlavorTierMessagePanel — sorts by that attribute rather than DOM
   * position) so they always read easiest-to-reach -> hardest-to-reach,
   * left to right.
   *
   * Box-count tiers and the free-shipping dollar-threshold tier aren't
   * naturally comparable — a fixed Liquid-rendered order (free shipping
   * always first) can put the actually-hardest tier first depending on
   * region (shipping_us_threshold vs shipping_intl_threshold) and purchase
   * type (subscription is ~50% off, so the same box count is worth half
   * the cents toward that threshold). Converts box counts to their
   * cents-equivalent using the reference per-box price so every tier ranks
   * on one scale instead.
   * @param {HTMLElement} modal
   * @param {number} total
   * @param {number} cents
   */
  function reorderFlavorTierSteps(modal, total, cents) {
    const track = modal.querySelector('.nw-new-bundle-flavors-tier__steps');
    if (!track) return;
    const steps = Array.from(track.querySelectorAll('li[data-tier-min-boxes]'));
    if (steps.length < 2) return;

    const perBoxCents = getFlavorTierReferencePerBoxCents(modal, total, cents);
    const ranked = steps
      .map((step) => {
        const freeShipping = step.getAttribute('data-tier-free-shipping') === 'true';
        const minBoxes = parsePositiveInt(step.getAttribute('data-tier-min-boxes'));
        const minCents = parsePositiveInt(step.getAttribute('data-tier-min-cents'));
        const rankCents = freeShipping && minCents > 0 ? minCents : minBoxes * perBoxCents;
        return { step, rankCents };
      })
      .sort((a, b) => a.rankCents - b.rankCents);

    ranked.forEach(({ step }, i) => {
      step.setAttribute('data-tier-slot', String(i + 1));
      // appendChild on an existing child moves it — this reorders in place
      // instead of duplicating.
      track.appendChild(step);
    });
  }

  function refreshFlavorsModalState() {
    const modal = getFlavorsModal();
    if (!modal) return;

    syncFlavorInputsWithBundleSellingPlan(modal);

    const selection = getFlavorsSelectionState(modal);
    const total = selection.totalQty;
    const cents = computeFlavorsSelectionCents(modal);
    const minBoxes = getMinTotalBoxesFromModal(modal);
    const promoPct = getFlavorsSubscriptionDiscountPercent(modal);
    const promoEl = modal.querySelector('[data-nw-flavor-header-promo]');
    if (promoEl && promoPct > 0) {
      promoEl.textContent = `${promoPct}% off first order`;
    }

    reorderFlavorTierSteps(modal, total, cents);

    modal.querySelectorAll('[data-tier-min-boxes]').forEach((step) => {
      if (!(step instanceof HTMLElement)) return;
      const min = parsePositiveInt(step.getAttribute('data-tier-min-boxes'));
      const minCents = parsePositiveInt(step.getAttribute('data-tier-min-cents'));
      const freeShipping = step.getAttribute('data-tier-free-shipping') === 'true';
      const unlocked = isV4FlavorsModal(modal)
        ? isV4FlavorTierUnlocked({ min, minCents, freeShipping }, total, cents)
        : isFlavorTierUnlocked({ min, minCents, freeShipping }, total, cents);
      step.classList.toggle('nw-new-bundle-flavors-tier__step--complete', unlocked);
      step.classList.toggle('nw-new-bundle-flavors-tier__step--pending', !unlocked);
      step.classList.toggle('nw-new-bundle-flavors-tier__step--unlocked', unlocked);
      step.classList.toggle('nw-new-bundle-flavors-tier__step--locked', !unlocked);
    });

    syncFlavorTierMessagePanel(modal, total, cents);
    updateFlavorTierTrackProgress(modal, total, cents);
    updateCartTierHeader(modal, total, cents);

    modal.querySelectorAll('.nw-new-bundle-flavors-item').forEach((row) => {
      if (!(row instanceof HTMLElement)) return;
      const input = row.querySelector('[data-flavor-input]');
      const minus = row.querySelector('[data-nw-flavor-qty-minus]');
      const plus = row.querySelector('[data-nw-flavor-qty-plus]');
      const addBtn = row.querySelector('[data-nw-flavor-add]');
      const qtyWrap = row.querySelector('.nw-new-bundle-flavors-item-action-quantity');

      if (input instanceof HTMLInputElement && addBtn instanceof HTMLButtonElement) {
        const collapsed = Boolean(qtyWrap?.classList.contains('hidden'));
        addBtn.disabled = isSoldOutLabelRow(row) || !collapsed;
      }

      if (!(input instanceof HTMLInputElement) || !(minus instanceof HTMLButtonElement)) return;
      if (!(plus instanceof HTMLButtonElement)) return;

      const collapsed = Boolean(qtyWrap?.classList.contains('hidden'));
      if (collapsed) {
        minus.disabled = true;
        plus.disabled = true;
        return;
      }

      const cur = parsePositiveInt(input.value);

      // See handleFlavorMinus: the PDP's bundle-minimum floor doesn't apply
      // in the cart-drawer upsell, so don't disable the button for it there.
      const minusDisabled =
        cur <= 0 || (!window.__nwUpsellFlavorsFlow && selection.totalQty - 1 < minBoxes);
      minus.disabled = minusDisabled;
      plus.disabled = false;
    });

    updateMainFlavorCtaState(modal, selection);
    updateCartMinBoxesMessage(modal, selection);
    updateFlavorsFooterSummary(modal);
  }

  /**
   * Clears checkout CTA loading state. Needed when the PDP is frozen in the back-forward cache
   * after redirecting to checkout (frozen DOM still had `loading`) — user hits Back and the loader
   * would otherwise remain. Also used on failed add-to-cart.
   */
  function resetFlavorsMainCtaLoadingUi() {
    const modal = getFlavorsModal();
    if (!modal) return;
    const btn = modal.querySelector('[data-main-cta]');
    if (!(btn instanceof HTMLButtonElement)) return;
    btn.classList.remove('loading');
    btn.removeAttribute('aria-busy');
    const spinner = btn.querySelector('.loading-overlay__spinner');
    if (spinner instanceof HTMLElement) spinner.classList.add('hidden');
    refreshFlavorsModalState();
  }

  /**
   * Variant metafield custom.sold_out_label: label below Add+ and disables it.
   * @param {HTMLElement | null} row
   * @returns {boolean}
   */
  function isSoldOutLabelRow(row) {
    return Boolean(row instanceof HTMLElement && row.getAttribute('data-sold-out-label') === 'true');
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  function handleFlavorAdd(btn) {
    const row = btn.closest('.nw-new-bundle-flavors-item');
    const modal = getFlavorsModal();
    if (!(row instanceof HTMLElement) || !modal) return;
    if (isSoldOutLabelRow(row)) return;

    expandFlavorQuantityRow(row);
    const input = row.querySelector('[data-flavor-input]');
    if (input instanceof HTMLInputElement) {
      const cur = parsePositiveInt(input.value);
      if (cur === 0) input.value = '1';
    }
    refreshFlavorsModalState();
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  function handleFlavorPlus(btn) {
    const row = btn.closest('.nw-new-bundle-flavors-item');
    const modal = getFlavorsModal();
    if (!(row instanceof HTMLElement) || !modal) return;

    const input = row.querySelector('[data-flavor-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const cur = parsePositiveInt(input.value);
    input.value = String(cur + 1);
    refreshFlavorsModalState();
  }

  /**
   * @param {HTMLButtonElement} btn
   */
  function handleFlavorMinus(btn) {
    const row = btn.closest('.nw-new-bundle-flavors-item');
    const modal = getFlavorsModal();
    if (!(row instanceof HTMLElement) || !modal) return;

    const input = row.querySelector('[data-flavor-input]');
    if (!(input instanceof HTMLInputElement)) return;

    const cur = parsePositiveInt(input.value);
    if (cur <= 0) return;

    // PDP flow enforces "select at least N boxes" as a bundle-purchase
    // minimum. The cart-drawer upsell isn't a bundle purchase — it's a
    // quick add-on to whatever's already in the cart — so that floor
    // doesn't apply there; let it go all the way down to 0.
    if (!window.__nwUpsellFlavorsFlow) {
      const selectionMinus = getFlavorsSelectionState(modal);
      const minBoxes = getMinTotalBoxesFromModal(modal);
      if (selectionMinus.totalQty - 1 < minBoxes) return;
    }

    input.value = String(cur - 1);
    const next = parsePositiveInt(input.value);
    if (next === 0) collapseFlavorQuantityRow(row);
    refreshFlavorsModalState();
  }

  function syncFlavorsModalPurchaseUi() {
    const modal = getFlavorsModal();
    if (!modal) return;

    const isSub = activePurchaseType === PURCHASE_SUBSCRIPTION;

    modal.querySelectorAll('.nw-new-bundle-flavors-item-price-subscription').forEach((el) => {
      el.classList.toggle('hidden', !isSub);
    });

    modal.querySelectorAll('.nw-new-bundle-flavors-item-price-onetime').forEach((el) => {
      el.classList.toggle('hidden', isSub);
    });

    updateFlavorsFooterSummary(modal);
  }

  function isFlavorsModalOpen() {
    const modal = getFlavorsModal();
    return Boolean(modal?.classList.contains(CLASS_NAME.flavorsModalOpen));
  }

  /** Match dialog `transition` duration in `nw-new-bundle.css` (backdrop + panel). */
  const FLAVORS_MODAL_SCROLL_UNLOCK_MS = 380;

  /** @type {ReturnType<typeof setTimeout> | null} */
  let flavorsModalScrollUnlockTimer = null;

  function releaseFlavorsModalScrollLock() {
    document.body.classList.remove(CLASS_NAME.bodyModalLock);
    clearModalScrollbarGapIfIdle();
  }

  function applyFlavorsModalScrollLock() {
    if (flavorsModalScrollUnlockTimer != null) {
      clearTimeout(flavorsModalScrollUnlockTimer);
      flavorsModalScrollUnlockTimer = null;
    }
    setModalScrollbarGapPx(measureModalScrollbarGapPx());
    document.body.classList.add(CLASS_NAME.bodyModalLock);
  }

  function scheduleFlavorsModalScrollUnlock() {
    if (flavorsModalScrollUnlockTimer != null) {
      clearTimeout(flavorsModalScrollUnlockTimer);
      flavorsModalScrollUnlockTimer = null;
    }
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;
    if (reduced) {
      releaseFlavorsModalScrollLock();
      return;
    }
    flavorsModalScrollUnlockTimer = setTimeout(() => {
      flavorsModalScrollUnlockTimer = null;
      releaseFlavorsModalScrollLock();
    }, FLAVORS_MODAL_SCROLL_UNLOCK_MS);
  }

  function syncJavvyFlavorsOffer(modal) {
    const slot = modal.querySelector('[data-nw-javvy-flavors-offer-slot]');
    if (!(slot instanceof HTMLElement)) return;
    const source = document.querySelector('.product--javvy [data-nw-javvy-flavors-offer]');
    slot.replaceChildren();
    if (source) {
      slot.appendChild(source.cloneNode(true));
      slot.hidden = false;
    } else {
      slot.hidden = true;
    }
  }

  function openFlavorsModal() {
    const modal = getFlavorsModal();
    if (!modal) return;

    // activePurchaseType is a single module-level variable shared with the
    // PDP's own purchase-type toggle (and persisted via a URL query param
    // across page loads) — since this modal is one sitewide singleton, a
    // prior PDP interaction elsewhere on the same page load can leave it
    // set to "onetime" by the time the cart-drawer opens it. Force
    // subscription explicitly here rather than relying on whatever the
    // "default" happens to be, since that default is exactly the stale
    // state this is meant to override.
    if (window.__nwUpsellFlavorsFlow) {
      activePurchaseType = PURCHASE_SUBSCRIPTION;
    }

    flavorsModalLastFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;

    syncJavvyFlavorsOffer(modal);

    modal.classList.add(CLASS_NAME.flavorsModalOpen);
    modal.setAttribute('aria-hidden', 'false');
    applyFlavorsModalScrollLock();

    syncFlavorsModalPurchaseUi();
    resetAndPrimeFlavorsModal();

    flavorsCartPrefetch = shouldClearCartBeforeFlavorCheckout(modal) ? null : fetchCartJson();

    const closeBtn = modal.querySelector('button[data-nw-bundle-flavors-close]');
    if (closeBtn instanceof HTMLElement) closeBtn.focus();

  }

  /**
   * @param {HTMLElement} modal
   */
  function dismissFlavorsSummaryPanel(modal) {
    const summaryPanel = modal.querySelector('[data-nw-flavor-summary-panel]');
    const summaryToggle = modal.querySelector('[data-nw-flavor-summary-toggle]');
    summaryPanel?.classList.remove('active');
    if (summaryToggle instanceof HTMLButtonElement) {
      summaryToggle.classList.remove('active');
      summaryToggle.setAttribute('aria-expanded', 'false');
    }
  }

  function closeFlavorsModal() {
    const modal = getFlavorsModal();
    if (!modal) return;

    const wasUpsellFlow = window.__nwUpsellFlavorsFlow;
    window.__nwUpsellFlavorsFlow = false;
    flavorsPdpClearCartOverride = null;
    if (wasUpsellFlow) {
      modal.classList.remove('is-cart-drawer-context');
      if (window.cartDrawerCustom && typeof window.cartDrawerCustom.open === 'function') {
        // viewOnly: restoring the drawer that was already open beneath this
        // modal — whether the upsell flavors were just added or the modal
        // was closed without adding, this isn't a fresh add-to-cart
        // decision for the *page's own* product, so it must not redirect to
        // checkout just because this page/product forces one.
        window.cartDrawerCustom.open(true);
      }
    }

    const wasOpen = modal.classList.contains(CLASS_NAME.flavorsModalOpen);

    dismissFlavorsSummaryPanel(modal);

    modal.classList.remove(CLASS_NAME.flavorsModalOpen);
    modal.setAttribute('aria-hidden', 'true');
    if (wasOpen) {
      scheduleFlavorsModalScrollUnlock();
    } else {
      releaseFlavorsModalScrollLock();
    }

    removePurchaseTypeFromUrl();
    activePurchaseType = readPurchaseTypeFromUrl() ?? PURCHASE_SUBSCRIPTION;

    if (flavorsModalLastFocus && document.contains(flavorsModalLastFocus)) {
      flavorsModalLastFocus.focus();
    }
    flavorsModalLastFocus = null;
  }

  /**
   * Optional `data-box-quantity` on `[data-nw-bundle-flavors-open]`: next `resetAndPrimeFlavorsModal` distributes
   * that box count across the first flavors (2 each) even when no PDP tier matches.
   * @param {Element} opener
   */
  function setFlavorsModalPrimeHintFromOpener(opener) {
    flavorsModalPrimeItemCountHint = null;
    if (!(opener instanceof Element)) return;
    const raw = opener.getAttribute(DATA_ATTR.boxQuantity);
    if (raw == null) return;
    const n = parsePositiveInt(String(raw).trim());
    if (n <= 0) return;
    flavorsModalPrimeItemCountHint = n;
  }

  /**
   * @param {MouseEvent} event
   */
  function onFlavorsModalPointerDown(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const ctaTrackEl = target.closest(SELECTOR.ctaTrack);
    if (ctaTrackEl) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'cta_click',
        cta_type: ctaTrackEl.getAttribute('data-nw-cta-track')
      });
    }

    const modalForFlavor = getFlavorsModal();
    if (isFlavorsModalOpen() && modalForFlavor) {
      const panel = modalForFlavor.querySelector('[data-nw-flavor-summary-panel]');
      if (panel instanceof HTMLElement && panel.classList.contains('active')) {
        const inPanel = target.closest('[data-nw-flavor-summary-panel]');
        const inToggle = target.closest('[data-nw-flavor-summary-toggle]');
        if (!inPanel && !inToggle) {
          dismissFlavorsSummaryPanel(modalForFlavor);
        }
      }
    }

    if (modalForFlavor && modalForFlavor.contains(target)) {
      const addBtn = target.closest('[data-nw-flavor-add]');
      if (addBtn instanceof HTMLButtonElement) {
        event.preventDefault();
        handleFlavorAdd(addBtn);
        return;
      }
      const minus = target.closest('[data-nw-flavor-qty-minus]');
      if (minus instanceof HTMLButtonElement) {
        event.preventDefault();
        handleFlavorMinus(minus);
        return;
      }
      const plus = target.closest('[data-nw-flavor-qty-plus]');
      if (plus instanceof HTMLButtonElement) {
        event.preventDefault();
        handleFlavorPlus(plus);
        return;
      }
      const summaryToggle = target.closest('[data-nw-flavor-summary-toggle]');
      if (summaryToggle instanceof HTMLButtonElement) {
        event.preventDefault();
        const panel = modalForFlavor.querySelector('[data-nw-flavor-summary-panel]');
        if (panel) {
          panel.classList.toggle('active');
          const open = panel.classList.contains('active');
          summaryToggle.classList.toggle('active', open);
          summaryToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        }
        return;
      }
      const mainCta = target.closest('[data-main-cta]');
      if (mainCta instanceof HTMLButtonElement) {
        event.preventDefault();
        void handleFlavorsMainCta(mainCta);
        return;
      }
    }

    if (target.closest(SELECTOR.flavorsModalOpen)) {
      event.preventDefault();
      const opener = target.closest(SELECTOR.flavorsModalOpen);
      if (opener instanceof Element) {
        const pt = normalizePurchaseType(opener.getAttribute('data-purchase-type'));
        if (pt) {
          activePurchaseType = pt;
          writePurchaseTypeToUrl(pt);
        }
        setFlavorsModalPrimeHintFromOpener(opener);
        flavorsPdpClearCartOverride = resolvePdpClearCartBeforeCheckoutFromOpener(opener);
      } else {
        flavorsPdpClearCartOverride = null;
      }
      const pdpModal = getFlavorsModal();
      if (pdpModal) pdpModal.classList.remove('is-cart-drawer-context');
      window.__nwUpsellFlavorsFlow = false;
      openFlavorsModal();
      return;
    }

    if (target.closest(SELECTOR.flavorsModalClose)) {
      event.preventDefault();
      closeFlavorsModal();
    }
  }

  /**
   * @param {KeyboardEvent} event
   */
  function onFlavorsModalKeydown(event) {
    if (event.key !== 'Escape') return;
    if (!isFlavorsModalOpen()) return;
    if (isCheckoutUpsellModalOpen()) return;
    event.preventDefault();
    closeFlavorsModal();
  }

  function bindFlavorsModalEvents() {
    document.addEventListener('click', onFlavorsModalPointerDown);
    document.addEventListener('keydown', onFlavorsModalKeydown);
  }

  /**
   * When the modal is rendered with `is-open` in Liquid, lock body scroll and focus the close control.
   * Remove the default `is-open` from the snippet when the modal should start closed.
   */
  function syncFlavorsModalOnLoad() {
    const modal = getFlavorsModal();
    if (!modal || !modal.classList.contains(CLASS_NAME.flavorsModalOpen)) return;

    applyFlavorsModalScrollLock();
    modal.setAttribute('aria-hidden', 'false');

    const closeBtn = modal.querySelector('button[data-nw-bundle-flavors-close]');
    if (closeBtn instanceof HTMLElement) {
      window.requestAnimationFrame(() => closeBtn.focus());
    }

    syncFlavorsModalPurchaseUi();
    resetAndPrimeFlavorsModal();
  }

  // ---------------------------------------------------------------------------
  // Events
  // ---------------------------------------------------------------------------

  /**
   * Gifts, CTA, and `?boxes=` from the **current** checked radio.
   * Does not read `?boxes=` back into the radios (so user clicks are not overwritten).
   * @param {HTMLInputElement | undefined} [bundleRadioFromEvent] Pass `event.target` from bundle radio `change`.
   */
  function syncBundleDependentUi(bundleRadioFromEvent) {
    applyPurchaseTypeFromUrl();
    syncCtaWithBundle();
    syncTreePlantingCountWithBundle();
    syncOnetimeBundleSummaryWithBundle();
    syncPercentageInfoWithBundle();
    syncBundleQuantityUrl(bundleRadioFromEvent);

    const fm = getFlavorsModal();
    if (fm) {
      syncFlavorInputsWithBundleSellingPlan(fm);
      if (isFlavorsModalOpen()) refreshFlavorsModalState();
    }

    if (getPurchaseTypeWidgetRoot()) syncPurchaseWidgetPricesFromBundleRadio();
  }

  /**
   * Apply `?boxes=` to radios, then run {@link syncBundleDependentUi}. Use on load / section reload only.
   */
  function syncBundleFromUrlAndRadios() {
    applyBundleSelectionFromUrl();
    syncBundleDependentUi();
  }

  /**
   * One delegated listener so dynamically replaced radios still work.
   */
  function onDocumentChange(event) {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.type === 'radio' && target.name === PURCHASE_TYPE_RADIO_NAME) {
      if (!target.closest(SELECTOR.purchaseTypeWidget)) return;
      const pt = normalizePurchaseType(target.value);
      if (!pt) return;
      activePurchaseType = pt;
      syncPurchaseTypeUrl();
      syncCtasPurchaseTypeAttrFromWidget();
      syncCtaWithBundle();
      syncFlavorsModalPurchaseUi();
      // Subscription vs one-time pricing changes the reference per-box
      // price reorderFlavorTierSteps ranks tiers by — re-run the full
      // refresh so tier order (and everything else it drives) stays
      // correct if this toggle fires while the modal is already open.
      // (Modal-open itself already runs a fresh refresh right after
      // priming, so this only matters for that already-open edge case.)
      const openModal = getFlavorsModal();
      if (openModal?.classList.contains(CLASS_NAME.flavorsModalOpen)) {
        refreshFlavorsModalState();
      }
      return;
    }

    if (target.name !== RADIO_NAME || target.type !== 'radio') return;
    syncBundleDependentUi(target);
  }

  function bindEvents() {
    document.addEventListener('change', onDocumentChange);
  }

  // ---------------------------------------------------------------------------
  // Bootstrap
  // ---------------------------------------------------------------------------

  /**
   * When Liquid doesn't bake a sub-discounted price into variant.price (e.g. the
   * gum-aftersell product used on the LP), the bundle-option radios render with
   * data-compare-price="" and data-price = full OTP price.
   *
   * If the global flavors modal dialog has data-flavors-subscription-discount-percent
   * set, we apply that % here to rewrite data-price / data-each-price / data-compare-price
   * and the visible price elements so the buy box matches the flavors modal.
   */
  function applySubDiscountToBundleOptionRadios() {
    const modal = getFlavorsModal();
    const dialog = modal?.querySelector('.nw-new-bundle-flavors-modal__dialog');
    const pct = getFlavorsSubscriptionDiscountPercent(modal);

    if (pct <= 0) return;

    const moneyFormat = String(
      dialog?.getAttribute('data-money-format') || window.Shopify?.money_format || '${amount}'
    );

    /**
     * @param {number} cents
     * @returns {string}
     */
    function formatMoneyCents(cents) {
      return formatCentsWithShopMoneyFormat(moneyFormat, cents)
        ?? `$${(Math.max(0, Math.round(cents)) / 100).toFixed(2)}`;
    }

    document.querySelectorAll(`input[name="${RADIO_NAME}"]`).forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      if (input.closest('.nw-new-bundle-options--javvy')) return;

      const compareRaw = (input.getAttribute(DATA_ATTR.comparePrice) ?? '').trim();
      // Only patch radios where Liquid left compare-price empty
      // (meaning no baked-in sub discount — all prices are OTP).
      if (compareRaw) return;

      const priceRaw = (input.getAttribute(DATA_ATTR.price) ?? '').trim();
      // data-price is formatted money like "$19.98" — parse cents from it.
      // Safer: re-derive from data-each-price × item-count.
      const eachRaw = (input.getAttribute(DATA_ATTR.eachPrice) ?? '').trim();
      const itemCount = Math.max(1, parsePositiveInt(input.getAttribute(DATA_ATTR.itemCount)) || 1);

      // Parse cents from formatted money string (strip non-numeric except dot).
      const parseMoneyStr = (s) => Math.round(parseFloat(s.replace(/[^0-9.]/g, '') || '0') * 100);

      const otpEachCents = parseMoneyStr(eachRaw || priceRaw);
      if (otpEachCents <= 0) return;

      const subEachCents = applySubscriptionDiscountPercent(otpEachCents, pct);
      const subTotalCents = subEachCents * itemCount;
      const otpTotalCents = otpEachCents * itemCount;

      // Rewrite data-attributes so JS price sync reads the discounted sub price.
      input.setAttribute(DATA_ATTR.price, formatMoneyCents(subTotalCents));
      input.setAttribute(DATA_ATTR.eachPrice, formatMoneyCents(subEachCents));
      input.setAttribute(DATA_ATTR.comparePrice, formatMoneyCents(otpTotalCents));
      input.setAttribute(DATA_ATTR.eachComparePrice, formatMoneyCents(otpEachCents));

      // Rewrite visible label elements.
      const label = document.querySelector(`label[for="${input.id}"]`);
      if (!label) return;

      // "/box" subtitle price
      const subtitlePrice = label.querySelector('.nw-new-bundle-option-label-item-title-subtitle-price');
      if (subtitlePrice) subtitlePrice.textContent = `${formatMoneyCents(subEachCents)}/box`;

      // Right-side price block: strike + current
      const priceText = label.querySelector('.nw-new-bundle-option-label-item-price-text');
      if (priceText) {
        let strike = priceText.querySelector('s');
        if (!strike) {
          strike = document.createElement('s');
          priceText.insertBefore(strike, priceText.firstChild);
        }
        strike.textContent = formatMoneyCents(otpTotalCents);
        const currentSpan = priceText.querySelector('span:not(s)') || priceText.querySelector('span');
        if (currentSpan) currentSpan.textContent = formatMoneyCents(subTotalCents);
      }

      // "Save $X" badge
      const saveBadge = label.querySelector('.nw-new-bundle-option-label-item-price-save-amount');
      const saveAmountCents = otpTotalCents - subTotalCents;
      if (saveAmountCents > 0) {
        if (saveBadge) {
          saveBadge.textContent = `Save ${formatMoneyCents(saveAmountCents)}`;
        } else {
          const titleRow = label.querySelector('.nw-new-bundle-option-label-item-title-row');
          if (titleRow) {
            const badge = document.createElement('div');
            badge.className = 'nw-new-bundle-option-label-item-price-save-amount';
            badge.textContent = `Save ${formatMoneyCents(saveAmountCents)}`;
            titleRow.appendChild(badge);
          }
        }
      }
    });

    // Re-sync CTA and one-time row after patching radios.
    syncCtaWithBundle();
    syncOnetimeBundleSummaryWithBundle();
    syncPercentageInfoWithBundle();
  }

  function init() {
    syncBundleFromUrlAndRadios();
    bindEvents();
    bindFlavorsModalEvents();
    bindCheckoutUpsellEvents();
    syncFlavorsModalOnLoad();
    syncFlavorsOrderDeadlineDates();
    applySubDiscountToBundleOptionRadios();

    /** bfcache: freeze strips loader before cache; restore clears any stuck state */
    function onPageLifecycleBfCacheFlavorCta(/** @type {PageTransitionEvent} */ event) {
      if (!event.persisted) return;
      resetFlavorsMainCtaLoadingUi();
      const upsellModal = getCheckoutUpsellModal();
      if (upsellModal) {
        upsellModal.querySelectorAll(SELECTOR.checkoutUpsellAccept).forEach((btn) => {
          if (btn instanceof HTMLButtonElement) resetCheckoutUpsellAcceptUi(btn);
        });
      }
    }
    window.addEventListener('pagehide', onPageLifecycleBfCacheFlavorCta);
    window.addEventListener('pageshow', onPageLifecycleBfCacheFlavorCta);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init, { once: true });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', () => {
    syncFlavorsOrderDeadlineDates();
    syncTreePlantingCountWithBundle();
    syncOnetimeBundleSummaryWithBundle();
    syncPercentageInfoWithBundle();
    applyPurchaseTypeFromUrl();
    syncCtaWithBundle();
    const fm = getFlavorsModal();
    if (fm) syncFlavorInputsWithBundleSellingPlan(fm);
  });

  window.NwNewBundle = {
    sync: syncBundleFromUrlAndRadios,
    syncCta: syncCtaWithBundle,
    syncTreePlantingCountWithBundle,
    syncOnetimeBundleSummaryWithBundle,
    getSelectedBundleItemCount,
    getActivePurchaseType,
    syncBundleQuantityUrl,
    syncPurchaseTypeUrl,
    openFlavorsModal,
    closeFlavorsModal,
    /** @returns {NwFlavorsSelection | null} */
    getFlavorsSelectionState() {
      const modal = getFlavorsModal();
      return modal ? getFlavorsSelectionState(modal) : null;
    },
    flavorsSelectionMeetsRequirements() {
      const modal = getFlavorsModal();
      if (!modal) return false;
      return flavorsSelectionMeetsRequirements(modal);
    },
    syncFlavorsOrderDeadlineDates,
    /** @param {HTMLElement | undefined} [modalEl] */
    syncFlavorsFooterSummary(modalEl) {
      const m = modalEl instanceof HTMLElement ? modalEl : getFlavorsModal();
      if (m) updateFlavorsFooterSummary(m);
    },
  };
})();
